import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShoppingCart, Plus, CreditCard, Receipt, Search, LogOut, User, Package, Trash2, Split, Banknote, Wallet, WifiOff, Printer, CheckCircle, XCircle, History, Image, X, Gift, Edit, Mail, Menu, Send } from 'lucide-react';
import axios from 'axios';

import { useAuth, API, getImageUrl } from '../contexts/AuthContext';
import { useOffline } from '../hooks/useOffline';
import { getErrorMessage } from '../utils/errorHandler';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import OfflineImage from '../components/OfflineImage';
import OrderHistoryModal from '../components/pos/OrderHistoryModal';
import { formatAmount } from '../utils/format';
import { calcTotals, itbisLabel } from '../utils/tax';

// El POS arranca con "En Local" y este cliente pre-escrito: en el mostrador la
// inmensa mayoria de ordenes son de gente que no esta registrada, y obligar a
// escribir un nombre para poder cobrar era un clic de mas en cada orden.
// Si el cajero teclea otro nombre, la busqueda y el alta de cliente funcionan igual.
const GENERIC_CUSTOMER_NAME = 'Cliente Genérico';
const isGenericCustomerName = (value) =>
  (value || '').trim().toLowerCase() === GENERIC_CUSTOMER_NAME.toLowerCase();

const POSDashboard = () => {
  const { user, logout, getAuthHeaders, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const [posId, setPosId] = useState(() => localStorage.getItem('pos_id') || '');
  const [showPosIdDialog, setShowPosIdDialog] = useState(false);
  const [posIdInput, setPosIdInput] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [orderType, setOrderType] = useState('dine_in'); // Default: En Local
  const [customerName, setCustomerName] = useState(GENERIC_CUSTOMER_NAME);
  const [customerPhone, setCustomerPhone] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all'); // Filtro por tipo de orden
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [selectedWaiter, setSelectedWaiter] = useState(''); // Mesero asignado para órdenes dine-in
  const [waiters, setWaiters] = useState([]); // Lista de meseros disponibles
  
  // Offline functionality
  const { 
    isOnline, 
    isOffline, 
    pendingActions, 
    cacheData, 
    getCachedData, 
    createOfflineOrder, 
    getOfflineOrders,
    syncOfflineActions 
  } = useOffline();
  const [offlineOrders, setOfflineOrders] = useState([]);
  
  // Cash Register states
  const [cashSession, setCashSession] = useState(null);
  const [showOpenCashDialog, setShowOpenCashDialog] = useState(false);
  const [showCloseCashDialog, setShowCloseCashDialog] = useState(false);
  const [showCashReportDialog, setShowCashReportDialog] = useState(false);
  const [unclosedRegister, setUnclosedRegister] = useState(null); // Para mostrar alerta de caja sin cerrar
  const [cashReport, setCashReport] = useState(null);
  const [cashSummary, setCashSummary] = useState(null); // Lo vendido según el sistema (cuadre)
  const [loadingCashSummary, setLoadingCashSummary] = useState(false);
  const [cashReportNeedsDialog, setCashReportNeedsDialog] = useState(false);
  const [openingAmount, setOpeningAmount] = useState('');
  const [closingData, setClosingData] = useState({
    declared_cash: '',
    declared_card: '',
    declared_transfer: ''
  });

  // Split Payment states
  const [isSplitPayment, setIsSplitPayment] = useState(false);
  const [splitPayments, setSplitPayments] = useState([
    { payment_method: 'cash', amount: '' }
  ]);

  // Order History Modal
  const [showOrderHistory, setShowOrderHistory] = useState(false);

  // Tenant bank info
  const [tenantInfo, setTenantInfo] = useState(null);

  // Transfer proof viewer
  const [showTransferProof, setShowTransferProof] = useState(false);
  const [currentTransferProof, setCurrentTransferProof] = useState(null);

  // Loyalty points redemption
  const [loyaltySearchName, setLoyaltySearchName] = useState('');
  const [loyaltyCustomer, setLoyaltyCustomer] = useState(null);
  // Cliente resuelto por TELÉFONO de la orden: fuente autorizada para el gating de crédito
  // (la búsqueda por nombre es poco fiable si el índice RediSearch falla por fallback SCAN).
  const [creditCustomer, setCreditCustomer] = useState(null);
  const [loyaltyConfig, setLoyaltyConfig] = useState(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);

  // Product variants selection
  const [showVariantSelector, setShowVariantSelector] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loyaltyDiscount, setLoyaltyDiscount] = useState(0);
  const [loadingLoyalty, setLoadingLoyalty] = useState(false);
  
  // Customer search in order form
  const [_searchingCustomer, _setSearchingCustomer] = useState(false);
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Create new customer modal
  const [showCreateCustomerDialog, setShowCreateCustomerDialog] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({
    name: '',
    phone: '',
    email: ''
  });
  
  // Edit order state
  const [editingOrder, setEditingOrder] = useState(null);

  // Invoice email
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [sendingInvoice, setSendingInvoice] = useState(false);

  // Helper: normaliza response.data a un array
  // Tolerante a formato paginado {data: [...], total: N} o undefined
  const normalizeArrayData = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.data)) return data.data;
    return [];
  };

  // Inicialización de sesión de caja y datos solo después de definir las funciones
  useEffect(() => {
    // Don't check cash session if user is logging out or user not loaded yet
    if (isLoggingOut || !user) return;
    
    const init = async () => {
      await checkCashSession();
      // Adopta la impresora POS configurada por el admin para imprimir sin diálogo
      autoConfigurePosPrinter();
    };
    init();
  }, [isLoggingOut, user]);

  useEffect(() => {
    if (cashSession) {
      loadOrders();
      loadProducts();
      loadCategories();
      loadTables();
      loadWaiters();
      loadCustomers();
      loadTenantInfo();
      loadLoyaltyConfig();
      
      // Auto-refresh órdenes cada 5 segundos
      const ordersInterval = setInterval(() => {
        loadOrders();
      }, 5000);
      
      return () => {
        clearInterval(ordersInterval);
      };
    }
  }, [cashSession]);

  const loadLoyaltyConfig = async () => {
    try {
      const response = await axios.get(`${API}/loyalty/config`, {
        headers: getAuthHeaders()
      });
      setLoyaltyConfig(response.data);
    } catch (error) {
      console.error('Error loading loyalty config:', error);
    }
  };

  const searchLoyaltyCustomer = async (name) => {
    if (!name || name.length < 2) {
      setLoyaltyCustomer(null);
      return;
    }
    
    setLoadingLoyalty(true);
    try {
      const response = await axios.get(`${API}/loyalty/customers/search`, {
        params: { name: name },
        headers: getAuthHeaders()
      });
      
      if (response.data) {
        setLoyaltyCustomer(response.data);
        setPointsToRedeem(0);
        setLoyaltyDiscount(0);
        toast.success(`Cliente encontrado: ${response.data.name}`);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setLoyaltyCustomer(null);
      } else {
        toast.error('Error al buscar cliente');
      }
      setLoyaltyCustomer(null);
    }
    setLoadingLoyalty(false);
  };
  
  // Búsqueda automática mientras escribe
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (loyaltySearchName && loyaltySearchName.length >= 2) {
        searchLoyaltyCustomer(loyaltySearchName);
      }
    }, 500); // Espera 500ms después de que el usuario deje de escribir
    
    return () => clearTimeout(delaySearch);
  }, [loyaltySearchName]);
  
  // Búsqueda automática de cliente en formulario de orden
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      // El nombre genérico pre-escrito NO es una busqueda: si se deja intacto,
      // el dropdown no debe abrirse encima del formulario al cargar el POS.
      if (customerName && customerName.trim().length >= 2 && !selectedCustomer && !isGenericCustomerName(customerName)) {
        // Siempre mostrar dropdown cuando hay 2+ caracteres
        // (para mostrar resultados o el botón "Crear Cliente")
        setShowCustomerDropdown(true);
      } else {
        setShowCustomerDropdown(false);
      }
    }, 300);
    
    return () => clearTimeout(delaySearch);
  }, [customerName, customerSearchResults, selectedCustomer]);

  const calculateLoyaltyDiscount = (points) => {
    if (!loyaltyConfig || !loyaltyCustomer) return 0;
    const maxPoints = loyaltyCustomer.total_points;
    const validPoints = Math.min(points, maxPoints);
    return validPoints * (loyaltyConfig.redemption_value || 0.05);
  };
  
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setCustomerName(customer.name);
    setCustomerPhone(customer.phone || '');
    setShowCustomerDropdown(false);
  };
  
  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    setCustomerName(GENERIC_CUSTOMER_NAME);
    setCustomerPhone('');
  };
  
  const handleCreateNewCustomer = async () => {
    if (!newCustomerData.name || !newCustomerData.phone) {
      toast.error('Nombre y teléfono son requeridos');
      return;
    }
    
    setLoading(true);
    try {
      // Crear cliente en el programa de fidelización
      await axios.post(
        `${API}/loyalty/customers`,
        {
          customer_name: newCustomerData.name,
          customer_phone: newCustomerData.phone,
          customer_email: newCustomerData.email
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Cliente creado exitosamente');
      
      // Recargar lista de clientes
      await loadCustomers();
      
      // Seleccionar el cliente recién creado
      const newCustomer = {
        id: newCustomerData.phone,
        name: newCustomerData.name,
        phone: newCustomerData.phone,
        email: newCustomerData.email,
        loyalty_points: 0
      };
      selectCustomer(newCustomer);
      
      // Cerrar modal y resetear datos
      setShowCreateCustomerDialog(false);
      setNewCustomerData({ name: '', phone: '', email: '' });
      
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear cliente'));
    }
    setLoading(false);
  };

  const openCreateCustomerDialog = () => {
    // Pre-llenar con el nombre que estaba buscando
    setNewCustomerData({
      name: customerName,
      phone: '',
      email: ''
    });
    setShowCustomerDropdown(false);
    setShowCreateCustomerDialog(true);
  };
  
  const handleEditOrder = (order) => {
    // Cargar la orden en el carrito para editarla
    setEditingOrder(order);
    setCart(order.items || []);
    setOrderType(order.order_type);
    setCustomerName(order.customer_name || '');
    setCustomerPhone(order.customer_phone || '');
    setSelectedTable(order.table_id || '');
    
    // Si tiene cliente, seleccionarlo
    if (order.customer_phone) {
      const customer = customerSearchResults.find(c => c.phone === order.customer_phone);
      if (customer) {
        setSelectedCustomer(customer);
      }
    }
    
    toast.info('Orden cargada para edición');
  };
  
  const handleCancelEdit = () => {
    setEditingOrder(null);
    setCart([]);
    setCustomerName(GENERIC_CUSTOMER_NAME);
    setCustomerPhone('');
    setSelectedCustomer(null);
    setSelectedTable('');
  };

  const handlePointsChange = (points) => {
    const numPoints = parseInt(points) || 0;
    if (loyaltyCustomer && numPoints > loyaltyCustomer.total_points) {
      setPointsToRedeem(loyaltyCustomer.total_points);
      setLoyaltyDiscount(calculateLoyaltyDiscount(loyaltyCustomer.total_points));
    } else {
      setPointsToRedeem(numPoints);
      setLoyaltyDiscount(calculateLoyaltyDiscount(numPoints));
    }
  };

  const applyLoyaltyDiscount = async () => {
    if (!loyaltyCustomer || pointsToRedeem <= 0) {
      toast.error('Selecciona puntos a canjear');
      return;
    }
    
    if (loyaltyConfig && pointsToRedeem < loyaltyConfig.min_points_redemption) {
      toast.error(`Mínimo ${loyaltyConfig.min_points_redemption} puntos para canjear`);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('phone', loyaltyCustomer?.phone || '');
      formData.append('points_to_redeem', pointsToRedeem);
      
      const response = await axios.post(`${API}/loyalty/redeem`, formData, {
        headers: getAuthHeaders()
      });
      
      setLoyaltyDiscount(response.data.discount_value);
      setLoyaltyCustomer({
        ...loyaltyCustomer,
        total_points: response.data.new_balance
      });
      
      toast.success(`¡Descuento de $${formatAmount(response.data.discount_value)} aplicado!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al canjear puntos');
    }
  };

  const clearLoyaltyData = () => {
    setLoyaltySearchName('');
    setLoyaltyCustomer(null);
    setCreditCustomer(null);
    setPointsToRedeem(0);
    setLoyaltyDiscount(0);
  };

  const loadTenantInfo = async () => {
    try {
      const response = await axios.get(`${API}/tenants/my-tenant`, {
        headers: getAuthHeaders()
      });
      setTenantInfo(response.data);
    } catch (error) {
      console.error('Error loading tenant info:', error);
    }
  };

  // Auto-configuración de impresión al abrir el POS: si el tenant tiene UNA
  // impresora de estación POS activa configurada (pestaña Impresoras), el
  // terminal adopta su pos_id. Así el ticket sale directo a esa impresora al
  // confirmar el pago de una factura y NO se abre el diálogo del navegador.
  // Con 0 o varias impresoras POS no se puede adivinar cuál es la del terminal,
  // así que se deja la configuración manual del "ID de caja".
  const autoConfigurePosPrinter = async () => {
    try {
      const { data } = await axios.get(`${API}/printers`, { headers: getAuthHeaders() });
      const posPrinters = (Array.isArray(data) ? data : []).filter(
        (p) => p?.station === 'pos' && p?.is_active !== false
      );
      if (posPrinters.length !== 1) return; // 0 o varias: nada que auto-configurar
      const printerPosId = (posPrinters[0].pos_id || '').trim();
      const currentPosId = (localStorage.getItem('pos_id') || '').trim();
      if (printerPosId === currentPosId) return; // ya está alineado
      if (printerPosId) {
        localStorage.setItem('pos_id', printerPosId);
        setPosId(printerPosId);
      } else {
        // La única impresora POS no tiene pos_id: el terminal tampoco debe llevar
        // uno, o la resolución filtraría por un ID que no coincide y abriría el
        // diálogo. Se limpia para que se use la impresora única.
        localStorage.removeItem('pos_id');
        setPosId('');
      }
    } catch (error) {
      console.error('Error al auto-configurar la impresora POS:', error);
    }
  };

  // Load offline orders when coming back online
  useEffect(() => {
    const loadOfflineOrdersList = async () => {
      const offline = await getOfflineOrders();
      setOfflineOrders(offline);
    };
    loadOfflineOrdersList();
  }, [getOfflineOrders, isOnline]);

  // Sync when coming back online
  useEffect(() => {
    if (isOnline && pendingActions > 0) {
      handleSyncOfflineOrders();
    }
  }, [isOnline]);

  const loadOrders = async () => {
    try {
      // Pedir SOLO la cola activa, igual que Meseros y que la grilla de Mesas del
      // admin. /orders sin filtro de estado es una vista de historial: la ventana de
      // paginacion se llena con las ordenes mas antiguas del tenant y una orden
      // recien abierta por el mesero nunca llegaba a caja.
      const response = await axios.get(
        `${API}/orders?status=pending&status=in_preparation&status=ready&limit=200`,
        { headers: getAuthHeaders() }
      );
      
      const ordersData = normalizeArrayData(response.data);
      
      console.info(`Total órdenes recibidas del backend: ${ordersData.length}`);
      
      // Debug: Mostrar status y payment_status de las primeras 5 órdenes
      console.info('Primeras 5 órdenes (debug):', ordersData.slice(0, 5).map(o => ({
        number: o.order_number,
        type: o.order_type,
        status: o.status,
        payment_status: o.payment_status,
        customer: o.customer_name
      })));
      
      // Filtrar órdenes que el cajero debe ver (red de seguridad sobre lo ya
      // filtrado en el backend): solo consumo en local y para llevar.
      // Las de delivery NO se muestran aquí: se cobran desde el panel de delivery.
      const visibleOrders = ordersData.filter((o) => {
        // EXCLUIR órdenes de delivery (solo mostrar dine_in y takeout)
        if (o.order_type === 'delivery') return false;
        
        // Mostrar órdenes pendientes
        if (o.status === 'pending') return true;
        
        // Mostrar órdenes en preparación
        if (o.status === 'in_preparation') return true;
        
        // Mostrar órdenes listas
        if (o.status === 'ready') return true;
        
        return false;
      });
      
      console.info(`Órdenes visibles para cajero: ${visibleOrders.length}`);
      console.info('Órdenes por tipo:', {
        dine_in: visibleOrders.filter(o => o.order_type === 'dine_in').length,
        takeout: visibleOrders.filter(o => o.order_type === 'takeout').length
      });
      
      // Merge with offline orders
      const offline = await getOfflineOrders();
      setOfflineOrders(offline);
      setOrders([...offline, ...visibleOrders]);
    } catch (error) {
      // If offline, load from cache
      if (isOffline) {
        const offline = await getOfflineOrders();
        setOfflineOrders(offline);
        setOrders(offline);
        toast.info('Mostrando órdenes offline');
      } else {
        toast.error(getErrorMessage(error, 'Error al cargar órdenes'));
      }
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: getAuthHeaders()
      });
      const productsData = normalizeArrayData(response.data);
      setProducts(productsData);
      // Cache products for offline use
      await cacheData('products', productsData);
    } catch (error) {
      // If offline, load from cache
      if (isOffline) {
        const cachedProducts = await getCachedData('products');
        if (cachedProducts.length > 0) {
          setProducts(cachedProducts);
          toast.info('Mostrando productos desde caché');
        } else {
          toast.error('No hay productos en caché');
        }
      } else {
        toast.error(getErrorMessage(error, 'Error al cargar productos'));
      }
    }
  };
  
  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: getAuthHeaders()
      });
      setCategories(normalizeArrayData(response.data));
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadTables = async () => {
    try {
      const response = await axios.get(`${API}/tables`, {
        headers: getAuthHeaders()
      });
      const tablesData = normalizeArrayData(response.data);
      setTables(tablesData);
      // Cache tables for offline use
      await cacheData('tables', tablesData);
    } catch (error) {
      // If offline, load from cache
      if (isOffline) {
        const cachedTables = await getCachedData('tables');
        if (cachedTables.length > 0) {
          setTables(cachedTables);
        }
      } else {
        toast.error(getErrorMessage(error, 'Error al cargar mesas'));
      }
    }
  };
  
  const loadWaiters = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeaders()
      });
      console.info('=== DEBUG: loadWaiters ===');
      const waitersData = normalizeArrayData(response.data);
      console.info('Total users:', waitersData.length);
      // Filtrar solo usuarios con rol "waiter"
      const waitersList = waitersData.filter(u => u.role === 'waiter' && u.is_active);
      console.info('Active waiters:', waitersList.length);
      console.info('Waiters:', waitersList);
      setWaiters(waitersList);
    } catch (error) {
      console.error('Error loading waiters:', error);
    }
  };
  
  const loadCustomers = async () => {
    try {
      // Buscar en el programa de fidelización en lugar de customers
      const response = await axios.get(`${API}/loyalty/customers`, {
        headers: getAuthHeaders()
      });
      const customersData = normalizeArrayData(response.data);
      console.info('Clientes del programa de fidelización cargados:', customersData.length);
      console.info('Primeros 3 clientes:', customersData.slice(0, 3));
      
      // Mapear los datos de customer_loyalty al formato esperado
      const mappedCustomers = customersData.map(c => ({
        id: c.customer_phone, // Usar phone como ID único
        name: c.customer_name || c.name,
        phone: c.customer_phone || c.phone,
        email: c.customer_email,
        loyalty_points: c.total_points || 0
      }));
      
      setCustomerSearchResults(mappedCustomers);
    } catch (error) {
      console.error('Error loading loyalty customers:', error);
    }
  };

  // Sync offline orders with server
  const handleSyncOfflineOrders = async () => {
    if (!isOnline || offlineOrders.length === 0) return;
    
    setLoading(true);
    // Delegar al hook: reenvía CREATE_ORDER al backend, marca markOrderSynced()
    // y elimina la acción de la cola (antes no se marcaba y se duplicaban)
    const { synced, failed } = await syncOfflineActions();
    
    if (synced > 0) {
      const offline = await getOfflineOrders();
      setOfflineOrders(offline);
      loadOrders();
    }
    if (failed > 0) {
      toast.error(`${failed} orden(es) fallaron al sincronizar`);
    }
    
    setLoading(false);
  };

  const checkCashSession = async () => {
    try {
      // Primero verificar si hay sesión en sessionStorage
      const storedSession = sessionStorage.getItem('cashSession');
      if (storedSession) {
        try {
          const session = JSON.parse(storedSession);
          // Verificar que la sesión sea del día actual y tenga un ID
          if (session.id && session.opening_time) {
            const sessionDate = new Date(session.opening_time).toDateString();
            const today = new Date().toDateString();
            
            if (sessionDate === today) {
              console.info('Sesión de caja recuperada de sessionStorage:', session);
              setCashSession(session);
              return;
            } else {
              // Sesión de otro día, limpiar
              console.info('Sesión de otro día, limpiando sessionStorage');
              sessionStorage.removeItem('cashSession');
            }
          }
        } catch (e) {
          console.error('Error parseando sesión:', e);
          sessionStorage.removeItem('cashSession');
        }
      }
      
      // Si no hay sesión en sessionStorage, consultar al backend
      console.info('Consultando sesión de caja al backend...');
      const response = await axios.get(`${API}/cash-register/current`, {
        headers: getAuthHeaders()
      });
      
      if (response.data) {
        console.info('Sesión de caja obtenida del backend:', response.data);
        setCashSession(response.data);
        // Guardar en sessionStorage
        sessionStorage.setItem('cashSession', JSON.stringify(response.data));
      } else {
        // No hay sesión abierta, mostrar modal para abrir
        console.info('No hay sesión activa, mostrando modal de apertura');
        setShowOpenCashDialog(true);
      }
    } catch (error) {
      console.error('Error checking cash session:', error);
      // If offline, allow working without cash session verification
      if (isOffline) {
        setCashSession({ id: 'offline_session', offline: true });
        toast.warning('Modo offline: Caja virtual activa');
      } else {
        setShowOpenCashDialog(true);
      }
    }
  };

  const handleOpenCash = async () => {
    if (!openingAmount || parseFloat(openingAmount) < 0) {
      toast.error('Por favor ingresa un monto inicial válido');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/cash-register/open`,
        { opening_amount: parseFloat(openingAmount) },
        { headers: getAuthHeaders() }
      );
      
      console.info('Caja abierta exitosamente:', response.data);
      setCashSession(response.data);
      // Guardar en sessionStorage
      sessionStorage.setItem('cashSession', JSON.stringify(response.data));
      setShowOpenCashDialog(false);
      setOpeningAmount('');
      setUnclosedRegister(null); // Limpiar alerta si existía
      toast.success('Caja abierta exitosamente');
    } catch (error) {
      // Verificar si es error de caja sin cerrar
      if (error.response?.status === 400 && error.response?.data?.detail?.error === 'unclosed_register') {
        const detail = error.response.data.detail;
        setUnclosedRegister(detail);
        setShowOpenCashDialog(false);
        toast.error(detail.message);
      } else {
        toast.error(getErrorMessage(error, 'Error al abrir caja'));
      }
    }
    setLoading(false);
  };

  // Cuadre de caja: el sistema informa cuánto cobró antes de que el cajero declare
  const loadCashSummary = async () => {
    setLoadingCashSummary(true);
    try {
      const response = await axios.get(`${API}/cash-register/summary`, {
        headers: getAuthHeaders()
      });
      setCashSummary(response.data);
    } catch (_error) {
      setCashSummary(null);
      toast.warning('No se pudo consultar lo vendido por el sistema; revisa antes de cerrar');
    }
    setLoadingCashSummary(false);
  };

  const openCloseCashDialog = () => {
    setShowCloseCashDialog(true);
    loadCashSummary();
  };

  // Ahorra la digitación: toma los montos que registró el sistema
  const fillDeclaredFromSystem = () => {
    if (!cashSummary) return;
    setClosingData({
      declared_cash: String(cashSummary.system_cash ?? 0),
      declared_card: String(cashSummary.system_card ?? 0),
      declared_transfer: String(cashSummary.system_transfer ?? 0)
    });
  };

  const handleCloseCash = async () => {
    const declaredCash = parseFloat(closingData.declared_cash) || 0;
    const declaredCard = parseFloat(closingData.declared_card) || 0;
    const declaredTransfer = parseFloat(closingData.declared_transfer) || 0;

    if (declaredCash < 0 || declaredCard < 0 || declaredTransfer < 0) {
      toast.error('Los montos no pueden ser negativos');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/cash-register/close`,
        {
          declared_cash: declaredCash,
          declared_card: declaredCard,
          declared_transfer: declaredTransfer,
          pos_id: localStorage.getItem('pos_id')?.trim() || null
        },
        { headers: getAuthHeaders() }
      );
      
      const result = response.data;
      
      // Determinar qué sesión se cerró (actual o anterior)
      const sessionToReport = cashSession || {
        opening_amount: unclosedRegister?.opening_amount || 0,
        opening_time: unclosedRegister?.date,
        id: unclosedRegister?.session_id
      };
      
      // Guardar el reporte para mostrar
      setCashReport({
        ...result,
        declared_cash: declaredCash,
        declared_card: declaredCard,
        declared_transfer: declaredTransfer,
        declared_total: declaredCash + declaredCard + declaredTransfer,
        opening_amount: sessionToReport.opening_amount,
        opening_time: sessionToReport.opening_time,
        closing_time: new Date().toISOString(),
        cashier_name: user?.name
      });
      
      setShowCloseCashDialog(false);
      setShowCashReportDialog(true);

      // El backend ya liberó las mesas ocupadas al cerrar la caja; recargar la
      // grilla para reflejarlo de inmediato en el POS.
      loadTables();

      // /cash-register/close ya intentó el cuadre en la impresora del POS; si no
      // lo confirmó se abre el diálogo del navegador con este mismo reporte
      if (result.cash_print?.status === 'sent') {
        toast.success('Cuadre enviado a la impresora del POS');
      } else {
        setCashReportNeedsDialog(true);
      }

      if (result.tables_freed > 0) {
        toast.success(`Caja cerrada. ${result.tables_freed} mesa(s) liberada(s).`);
      }
      
      // Si se cerró la caja anterior, limpiar la alerta
      if (unclosedRegister) {
        setUnclosedRegister(null);
        toast.success('Caja anterior cerrada. Ahora puedes abrir una nueva caja.');
      } else {
        setCashSession(null);
        // Limpiar sessionStorage
        sessionStorage.removeItem('cashSession');
        console.info('Caja cerrada, sesión limpiada');
      }
      
      setClosingData({ declared_cash: '', declared_card: '', declared_transfer: '' });
      
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cerrar caja'));
    }
    setLoading(false);
  };

  const printCashReport = () => {
    if (!cashReport) return;
    
    const printWindow = window.open('', '_blank');
    const difference = cashReport.difference || 0;
    const differenceClass = difference >= 0 ? 'positive' : 'negative';
    const differenceText = difference >= 0 ? 'SOBRANTE' : 'FALTANTE';

    // Restaurant info from tenant
    const restaurantName = tenantInfo?.name || 'Restaurante';
    const restaurantAddress = tenantInfo?.address || '';
    const restaurantPhone = tenantInfo?.phone || '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Reporte de Cierre de Caja</title>
        <style>
          body {
            font-family: 'Courier New', monospace;
            max-width: 300px;
            margin: 0 auto;
            padding: 20px;
            font-size: 12px;
          }
          .restaurant-header {
            text-align: center;
            margin-bottom: 10px;
          }
          .restaurant-header h1 {
            font-size: 14px;
            margin: 0 0 5px 0;
          }
          .restaurant-header p {
            margin: 2px 0;
            font-size: 9px;
          }
          .header {
            text-align: center;
            border-bottom: 2px dashed #000;
            border-top: 2px dashed #000;
            padding: 10px 0;
            margin: 10px 0;
          }
          .header h2 {
            font-size: 16px;
            margin: 0;
          }
          .header p {
            margin: 5px 0;
            font-size: 10px;
          }
          .section {
            margin: 15px 0;
            padding: 10px 0;
            border-bottom: 1px dashed #ccc;
          }
          .section-title {
            font-weight: bold;
            margin-bottom: 8px;
            text-transform: uppercase;
          }
          .row {
            display: flex;
            justify-content: space-between;
            margin: 4px 0;
          }
          .row.total {
            font-weight: bold;
            border-top: 1px solid #000;
            padding-top: 5px;
            margin-top: 10px;
          }
          .difference {
            text-align: center;
            padding: 10px;
            margin: 15px 0;
            border: 2px solid #000;
          }
          .difference.positive {
            background: #e8f5e9;
          }
          .difference.negative {
            background: #ffebee;
          }
          .difference .label {
            font-size: 10px;
          }
          .difference .value {
            font-size: 18px;
            font-weight: bold;
          }
          .footer {
            text-align: center;
            margin-top: 20px;
            font-size: 10px;
          }
          @media print {
            body { margin: 0; padding: 10px; }
          }
        </style>
      </head>
      <body>
        <div class="restaurant-header">
          <h1>${restaurantName}</h1>
          ${restaurantAddress ? `<p>📍 ${restaurantAddress}</p>` : ''}
          ${restaurantPhone ? `<p>📞 ${restaurantPhone}</p>` : ''}
        </div>
        
        <div class="header">
          <h2>CIERRE DE CAJA</h2>
          <p>Cajero: ${cashReport.cashier_name || 'N/A'}</p>
          <p>Apertura: ${new Date(cashReport.opening_time).toLocaleString('es-ES')}</p>
          <p>Cierre: ${new Date(cashReport.closing_time).toLocaleString('es-ES')}</p>
        </div>

        <div class="section">
          <div class="section-title">💵 Ventas del Sistema</div>
          <div class="row">
            <span>Efectivo:</span>
            <span>$${formatAmount(cashReport.system_cash)}</span>
          </div>
          <div class="row">
            <span>Tarjeta:</span>
            <span>$${formatAmount(cashReport.system_card)}</span>
          </div>
          <div class="row">
            <span>Transferencia:</span>
            <span>$${formatAmount(cashReport.system_transfer)}</span>
          </div>
          <div class="row total">
            <span>TOTAL SISTEMA:</span>
            <span>$${formatAmount(cashReport.system_total)}</span>
          </div>
          <div class="row">
            <span>Cobros:</span>
            <span>${cashReport.tickets_count ?? 0}</span>
          </div>
          <div class="row">
            <span>Efecto. esperado cajón:</span>
            <span>$${formatAmount(cashReport.expected_cash)}</span>
          </div>
        </div>

        <div class="section">
          <div class="section-title">📝 Declarado por Cajero</div>
          <div class="row">
            <span>Efectivo:</span>
            <span>$${formatAmount(cashReport.declared_cash)}</span>
          </div>
          <div class="row">
            <span>Tarjeta:</span>
            <span>$${formatAmount(cashReport.declared_card)}</span>
          </div>
          <div class="row">
            <span>Transferencia:</span>
            <span>$${formatAmount(cashReport.declared_transfer)}</span>
          </div>
          <div class="row total">
            <span>TOTAL DECLARADO:</span>
            <span>$${formatAmount(cashReport.declared_total)}</span>
          </div>
        </div>

        <div class="difference ${differenceClass}">
          <div class="label">${differenceText}</div>
          <div class="value">$${formatAmount(Math.abs(difference))}</div>
        </div>

        <div class="footer">
          <p>--- Reporte generado automáticamente ---</p>
          <p>${new Date().toLocaleString('es-ES')}</p>
        </div>

        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Cuadre en la impresora POS del terminal: sin diálogo del navegador.
  const printCashReportViaPrinter = async (sessionId) => {
    if (!sessionId) return false;
    try {
      const response = await axios.post(
        `${API}/cash-register/${sessionId}/print-report`,
        { pos_id: localStorage.getItem('pos_id')?.trim() || null },
        { headers: getAuthHeaders() }
      );
      const status = response.data?.status;
      if (status === 'sent') {
        toast.success('Cuadre enviado a la impresora del POS');
        return true;
      }
      if (status === 'error') {
        toast.warning('La impresora del POS no confirmó el trabajo; se abrirá el diálogo de impresión');
      }
      return false;
    } catch (_error) {
      // Sin servicio de impresión en el equipo: se abre el diálogo del navegador
      return false;
    }
  };

  // Flujo completo del cuadre: impresora configurada > diálogo del navegador
  const printCashReportWithFallback = async (report) => {
    const printed = await printCashReportViaPrinter(report?.session_id || cashReport?.session_id);
    if (!printed) {
      printCashReport();
    }
  };

  // El cierre sin impresora disponible necesita el reporte ya en pantalla para
  // imprimirse por el navegador
  useEffect(() => {
    if (cashReport && cashReportNeedsDialog) {
      setCashReportNeedsDialog(false);
      printCashReport();
    }
  }, [cashReport, cashReportNeedsDialog]);

  // Imprimir Comanda de Cocina
  const _printComanda = async (order) => {
    try {
      // Obtener comandas del backend
      const response = await axios.post(
        `${API}/orders/${order.id}/comandas`,
        {},
        { headers: getAuthHeaders() }
      );
      
      const comandas = response.data.comandas || [];
      
      if (comandas.length === 0) {
        toast.info('No hay comandas para imprimir');
        return;
      }
      
      // Abrir ventana de impresión para cada comanda
      for (const comanda of comandas) {
        // Convertir base64 a blob y abrir
        if (comanda.pdf_base64) {
          const pdfWindow = window.open('', '_blank');
          pdfWindow.document.write(`
            <html>
            <head><title>Comanda ${comanda.station_name}</title></head>
            <body style="margin:0;padding:0;">
              <embed src="data:application/pdf;base64,${comanda.pdf_base64}" type="application/pdf" width="100%" height="100%" />
            </body>
            </html>
          `);
        }
      }
      
      toast.success(`${comandas.length} comanda(s) generada(s)`);
    } catch (_error) {
      // Si falla el endpoint, generar comanda simple HTML
      printComandaHTML(order);
    }
  };

  // Imprimir Comanda en formato HTML (fallback)
  const printComandaHTML = (order) => {
    const printWindow = window.open('', '_blank');
    
    const orderTypeNames = {
      'dine_in': 'EN LOCAL',
      'takeout': 'PARA LLEVAR',
      'delivery': 'DELIVERY'
    };

    // Restaurant info from tenant
    const restaurantName = tenantInfo?.name || 'Restaurante';
    
    const tableName = order.table_id 
      ? tables.find(t => t.id === order.table_id)?.name || `Mesa ${tables.find(t => t.id === order.table_id)?.number || ''}` 
      : '';
    
    const itemsHTML = order.items?.map(item => `
      <div class="item">
        <span class="qty">${item.quantity}x</span>
        <span class="name">${item.product_name}</span>
        ${item.notes ? `<div class="notes">→ ${item.notes}</div>` : ''}
      </div>
    `).join('') || '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Comanda #${order.order_number}</title>
        <style>
          @page {
            size: 58mm auto;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', monospace;
            width: 58mm;
            margin: 0 auto;
            padding: 2mm;
            font-size: 9px;
            line-height: 1.2;
          }
          .restaurant-name {
            text-align: center;
            font-size: 10px;
            font-weight: bold;
            margin-bottom: 1mm;
          }
          .header {
            text-align: center;
            border-bottom: 2px dashed #000;
            padding-bottom: 2mm;
            margin-bottom: 2mm;
          }
          .header h1 {
            font-size: 14px;
            margin: 0;
          }
          .header h2 {
            font-size: 16px;
            margin: 1mm 0;
            background: #000;
            color: #fff;
            padding: 1mm;
          }
          .info {
            margin: 2mm 0;
            padding: 1mm 0;
            border-bottom: 1px dashed #ccc;
            font-size: 8px;
          }
          .info p {
            margin: 0.5mm 0;
          }
          .table-name {
            font-size: 12px;
            font-weight: bold;
            background: #f0f0f0;
            padding: 1mm;
            text-align: center;
            margin: 2mm 0;
          }
          .items {
            margin: 2mm 0;
          }
          .item {
            padding: 1.5mm 0;
            border-bottom: 1px dotted #ccc;
          }
          .item .qty {
            font-weight: bold;
            font-size: 11px;
          }
          .item .name {
            font-size: 10px;
            font-weight: bold;
          }
          .item .notes {
            font-size: 8px;
            color: #666;
            margin-left: 3mm;
            font-style: italic;
          }
          .footer {
            text-align: center;
            margin-top: 3mm;
            padding-top: 2mm;
            border-top: 2px dashed #000;
            font-size: 8px;
          }
          @media print {
            body { width: 58mm; margin: 0; padding: 2mm; }
          }
        </style>
      </head>
      <body>
        <div class="restaurant-name">${restaurantName}</div>
        <div class="header">
          <h1>COCINA</h1>
          <h2>ORDEN #${order.order_number}</h2>
        </div>
        
        <div class="info">
          <p><b>${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES', {hour: '2-digit', minute:'2-digit'})}</b></p>
          <p>${orderTypeNames[order.order_type] || order.order_type}</p>
          ${order.customer_name ? `<p>Cliente: ${order.customer_name}</p>` : ''}
        </div>
        
        ${tableName ? `<div class="table-name">${tableName}</div>` : ''}
        
        <div class="items">
          ${itemsHTML}
        </div>
        
        <div class="footer">
          <p>--- COMANDA ---</p>
          <p>${new Date().toLocaleTimeString('es-ES')}</p>
        </div>
        
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    toast.success('Comanda enviada a impresión');
  };

  // Imprime en la impresora POS configurada: sin diálogo del navegador.
  // Devuelve false cuando el terminal no tiene impresora (o la impresión falló)
  // y hay que abrir el diálogo.
  const printReceiptViaPrinter = async (order) => {
    if (!order?.id) return false;
    try {
      const response = await axios.post(
        `${API}/orders/${order.id}/print-receipt`,
        { pos_id: localStorage.getItem('pos_id')?.trim() || null },
        { headers: getAuthHeaders() }
      );
      const status = response.data?.status;
      if (status === 'sent') {
        toast.success('Ticket enviado a la impresora del POS');
        return true;
      }
      if (status === 'error') {
        toast.warning('La impresora del POS no confirmó el trabajo; se abrirá el diálogo de impresión');
      }
      return false;
    } catch (_error) {
      // Sin servicio de impresión en el equipo: se abre el diálogo del navegador
      return false;
    }
  };

  // Flujo completo del ticket: impresora configurada > diálogo del navegador
  const printReceiptWithFallback = async (order) => {
    const printed = await printReceiptViaPrinter(order);
    if (!printed) {
      printReceipt(order);
    }
  };

  // Mesa y mesero de la orden: es el mismo detalle que agrega el backend al ticket
  const getTicketTableLabel = (order) => {
    if (!order?.table_id) return order?.table_name || order?.table_number || '';
    const table = tables.find(t => t.id === order.table_id);
    return table?.name || table?.number || order?.table_name || '';
  };

  const getTicketWaiterName = (order) => {
    if (order?.waiter_name) return order.waiter_name;
    return waiters.find(w => w.id === order?.waiter_id)?.name || '';
  };

  // Imprimir Ticket/Recibo de Venta
  const printReceipt = (order) => {
    const printWindow = window.open('', '_blank');
    const tableLabel = getTicketTableLabel(order);
    const waiterName = getTicketWaiterName(order);
    
    const orderTypeNames = {
      'dine_in': 'En Local',
      'takeout': 'Para Llevar',
      'delivery': 'Delivery'
    };
    
    const paymentMethodNames = {
      'cash': 'Efectivo',
      'card': 'Tarjeta',
      'transfer': 'Transferencia',
      'credit': 'CRÉDITO'
    };

    // Restaurant info from tenant
    const restaurantName = tenantInfo?.name || 'Restaurante';
    const restaurantAddress = tenantInfo?.address || '';
    const restaurantPhone = tenantInfo?.phone || '';
    const _restaurantEmail = tenantInfo?.email || '';
    const _taxRate = tenantInfo?.tax_rate || 0;
    
    const itemsHTML = order.items?.map(item => `
      <tr>
        <td class="qty">${item.quantity}</td>
        <td class="desc">${item.product_name}${item.notes ? `<br><small style="font-size:6px;color:#666">${item.notes}</small>` : ''}</td>
        <td class="price">${formatAmount(item.subtotal)}</td>
      </tr>
    `).join('') || '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ticket #${order.order_number}</title>
        <style>
          @page {
            size: 58mm auto;
            margin: 0;
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', monospace;
            width: 58mm;
            margin: 0 auto;
            padding: 3mm;
            font-size: 9px;
            line-height: 1.2;
          }
          .header {
            text-align: center;
            margin-bottom: 3mm;
          }
          .header h1 {
            font-size: 12px;
            margin: 0 0 1mm 0;
            font-weight: bold;
          }
          .header .restaurant-info {
            font-size: 7px;
            color: #333;
          }
          .header .restaurant-info p {
            margin: 0.5mm 0;
          }
          .divider {
            border-top: 1px dashed #000;
            margin: 2mm 0;
          }
          .info {
            margin: 2mm 0;
            font-size: 8px;
          }
          .info p {
            margin: 0.5mm 0;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8px;
          }
          th {
            font-size: 7px;
            border-bottom: 1px solid #000;
            padding: 1mm 0;
          }
          td {
            padding: 1mm 0;
            vertical-align: top;
          }
          .qty {
            width: 8mm;
            text-align: center;
          }
          .desc {
            text-align: left;
            max-width: 35mm;
            word-wrap: break-word;
          }
          .price {
            width: 12mm;
            text-align: right;
          }
          .total-section {
            margin-top: 2mm;
            padding-top: 2mm;
            border-top: 1px dashed #000;
            font-size: 8px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin: 0.5mm 0;
          }
          .total-row.grand-total {
            font-size: 10px;
            font-weight: bold;
            margin-top: 1mm;
            padding-top: 1mm;
            border-top: 1px solid #000;
          }
          .footer {
            text-align: center;
            margin-top: 3mm;
            padding-top: 2mm;
            border-top: 1px dashed #000;
            font-size: 7px;
          }
          .points-box {
            margin: 2mm 0;
            padding: 1.5mm;
            background: #f3e8ff;
            border-radius: 2px;
          }
          .points-box p {
            margin: 0;
            font-size: 8px;
          }
          @media print {
            body { 
              width: 58mm; 
              margin: 0; 
              padding: 2mm; 
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${restaurantName}</h1>
          <div class="restaurant-info">
            ${restaurantAddress ? `<p>${restaurantAddress}</p>` : ''}
            ${restaurantPhone ? `<p>Tel: ${restaurantPhone}</p>` : ''}
          </div>
        </div>
        
        <div class="divider"></div>
        
        <div class="info">
          <p><b>Ticket #${order.order_number}</b></p>
          <p>${new Date().toLocaleDateString('es-ES')} ${new Date().toLocaleTimeString('es-ES', {hour: '2-digit', minute:'2-digit'})}</p>
          <p>${orderTypeNames[order.order_type] || order.order_type}</p>
          ${tableLabel ? `<p>Mesa: ${tableLabel}</p>` : ''}
          ${waiterName ? `<p>Mesero: ${waiterName}</p>` : ''}
          ${order.customer_name ? `<p>Cliente: ${order.customer_name}</p>` : ''}
          ${order.payment_method ? `<p>Pago: ${paymentMethodNames[order.payment_method] || order.payment_method}</p>` : ''}
        </div>
        
        <div class="divider"></div>
        
        <table>
          <thead>
            <tr>
              <th class="qty">Cant</th>
              <th class="desc">Producto</th>
              <th class="price">$</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>
        
        <div class="total-section">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>$${formatAmount(order.subtotal || order.total)}</span>
          </div>
          ${order.tax ? `
          <div class="total-row">
            <span>ITBIS:</span>
            <span>$${formatAmount(order.tax)}</span>
          </div>
          ` : ''}
          ${order.discount ? `
          <div class="total-row">
            <span>Desc:</span>
            <span>-$${formatAmount(order.discount)}</span>
          </div>
          ` : ''}
          ${order.loyalty_discount ? `
          <div class="total-row" style="color: #7c3aed;">
            <span>Puntos:</span>
            <span>-$${formatAmount(order.loyalty_discount)}</span>
          </div>
          ` : ''}
          <div class="total-row grand-total">
            <span>TOTAL</span>
            <span>$${order.final_total ? formatAmount(order.final_total) : formatAmount(order.total)}</span>
          </div>
        </div>
        
        <div class="footer">
          <p>¡Gracias por su preferencia!</p>
          ${order.points_earned ? `
          <div class="points-box">
            <p style="color: #7c3aed; font-weight: bold;">★ Puntos ganados: ${order.points_earned}</p>
          </div>
          ` : ''}
        </div>
        
        <script>
          window.onload = function() {
            window.print();
          }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    toast.success('Recibo enviado a impresión');
  };

  const addToCart = (product) => {
    // Si el producto tiene variantes, mostrar selector
    if (product.variants && product.variants.length > 0) {
      setSelectedProduct(product);
      setSelectedVariant(null);
      setShowVariantSelector(true);
      return;
    }
    
    // Si no tiene variantes, agregar directamente
    const existingItem = cart.find(item => item.id === product.id && !item.variant_id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id && !item.variant_id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success('Producto agregado');
  };

  const addVariantToCart = () => {
    if (!selectedVariant) {
      toast.error('Selecciona una variante');
      return;
    }
    
    // Buscar si ya existe este producto con esta variante específica
    const existingItem = cart.find(item => 
      item.id === selectedProduct.id && item.variant_id === selectedVariant.id
    );
    
    // Crear nombre combinado: producto + variante
    const combinedName = `${selectedProduct.name} ${selectedVariant.name}`;
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === selectedProduct.id && item.variant_id === selectedVariant.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { 
        ...selectedProduct,
        variant_id: selectedVariant.id,
        variant_name: selectedVariant.name,
        product_name: combinedName,
        name: combinedName,
        price: selectedVariant.price,
        quantity: 1
      }]);
    }
    
    toast.success(`${combinedName} agregado`);
    setShowVariantSelector(false);
    setSelectedProduct(null);
    setSelectedVariant(null);
  };

  const removeFromCart = (productId, variantId = null) => {
    setCart(cart.filter(item => {
      if (variantId) {
        return !(item.id === productId && item.variant_id === variantId);
      }
      return !(item.id === productId && !item.variant_id);
    }));
  };

  const updateCartQuantity = (productId, quantity, variantId = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, variantId);
    } else {
      setCart(cart.map(item => {
        if (variantId) {
          return (item.id === productId && item.variant_id === variantId) ? { ...item, quantity } : item;
        }
        return (item.id === productId && !item.variant_id) ? { ...item, quantity } : item;
      }));
    }
  };

  const updateCartItemNotes = (productId, notes, variantId = null) => {
    setCart(cart.map(item => {
      if (variantId) {
        return (item.id === productId && item.variant_id === variantId) ? { ...item, notes } : item;
      }
      return (item.id === productId && !item.variant_id) ? { ...item, notes } : item;
    }));
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

    const handleCreateOrder = async () => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    // Validar cliente
    if (!customerName || customerName.trim() === '') {
      toast.error('Debes ingresar el nombre del cliente');
      return;
    }

    // Validar mesa para órdenes en local
    if (orderType === 'dine_in' && !selectedTable) {
      toast.error('Selecciona una mesa para órdenes en local');
      return;
    }

    // Validar mesero para órdenes en local
    if (orderType === 'dine_in' && !selectedWaiter) {
      toast.error('Selecciona un mesero para órdenes en local');
      return;
    }

    setLoading(true);
    
    // Prepare order data
    const simpleItems = cart.map(item => ({
      product_id: item.id,
      variant_id: item.variant_id || null,
      quantity: item.quantity,
      notes: item.notes || ''
    }));
    
    const orderData = {
      order_type: orderType,
      table_id: orderType === 'dine_in' ? selectedTable : null,
      waiter_id: orderType === 'dine_in' ? selectedWaiter : null,
      customer_name: customerName,
      customer_phone: customerPhone || null,
      items: simpleItems,
      discount: 0
    };

    // If editing an existing order
    if (editingOrder) {
      try {
        await axios.put(
          `${API}/orders/${editingOrder.id}`,
          orderData,
          { headers: getAuthHeaders() }
        );
        
        toast.success('Orden actualizada exitosamente');
        setCart([]);
        setCustomerName(GENERIC_CUSTOMER_NAME);
        setCustomerPhone('');
        setSelectedCustomer(null);
        setSelectedTable('');
        setSelectedWaiter('');
        setEditingOrder(null);
        loadOrders();
        loadTables();
      } catch (error) {
        toast.error(getErrorMessage(error, 'Error al actualizar orden'));
      }
      setLoading(false);
      return;
    }

    // If offline, create order locally
    if (isOffline) {
      try {
        // Calculate total from cart
        const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
        
        const offlineOrder = await createOfflineOrder({
          ...orderData,
          items: cart.map(item => ({
            product_id: item.id,
            product_name: item.name,
            quantity: item.quantity,
            price: item.price,
            subtotal: item.price * item.quantity,
            notes: item.notes || ''
          })),
          total,
          status: 'pending',
          order_number: `OFF-${Date.now().toString().slice(-6)}`,
          table_name: tables.find(t => t.id === selectedTable)?.name || null
        });
        
        toast.success('Orden creada offline', {
          description: 'Se sincronizará cuando haya conexión',
          icon: '📴'
        });
        
        setCart([]);
        setCustomerName(GENERIC_CUSTOMER_NAME);
        setCustomerPhone('');
        setSelectedCustomer(null);
        setSelectedTable('');
        setSelectedWaiter('');
        setOfflineOrders(prev => [...prev, offlineOrder]);
        setOrders(prev => [offlineOrder, ...prev]);
      } catch (_error) {
        toast.error('Error al crear orden offline');
      }
      setLoading(false);
      return;
    }

    // Online: create order on server
    try {
      await axios.post(
        `${API}/orders`,
        orderData,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Orden creada exitosamente');
      setCart([]);
      setCustomerName(GENERIC_CUSTOMER_NAME);
      setCustomerPhone('');
      setSelectedCustomer(null);
      setSelectedTable('');
      setSelectedWaiter('');
      loadOrders();
      loadTables(); // Recargar mesas para actualizar estados
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al crear orden'));
    }
    setLoading(false);
  };

  const handleOpenPayment = async (order) => {
    // Solo el cajero puede procesar pagos
    if (user?.role !== 'cashier') {
      toast.error('Solo el cajero puede procesar pagos');
      return;
    }

    setSelectedOrder(order);
    setIsSplitPayment(false);
    setSplitPayments([{ payment_method: 'cash', amount: '' }]);
    // Cargar el método de pago de la orden si existe, si no usar 'cash'
    setPaymentMethod(order.payment_method || 'cash');
    setInvoiceEmail('');
    // Resolver el cliente por teléfono para el gating de CRÉDITO (endpoint fiable)
    setCreditCustomer(null);
    if (order.customer_phone) {
      try {
        const custRes = await axios.get(`${API}/loyalty/customers/${order.customer_phone}`, {
          headers: getAuthHeaders()
        });
        if (custRes.data && custRes.data.exists !== false) {
          setCreditCustomer(custRes.data);
        }
      } catch (_error) {
        // Sin ficha de fidelidad por teléfono: el gating lo tratará como no habilitado
      }
    }
    
    // Cargar información de fidelización si la orden tiene cliente real
    // (con el nombre genérico no se consulta: no existe en el programa y solo
    // sería una petición al vacio cada vez que se abre un cobro)
    if (order.customer_name && !isGenericCustomerName(order.customer_name)) {
      setLoyaltySearchName(order.customer_name);
      try {
        setLoadingLoyalty(true);
        const response = await axios.get(`${API}/loyalty/customers/search`, {
          params: { name: order.customer_name },
          headers: getAuthHeaders()
        });
        // Verificar si el cliente existe en el programa de fidelidad
        if (response.data && response.data.exists !== false) {
          setLoyaltyCustomer(response.data);
          setPointsToRedeem(0);
          setLoyaltyDiscount(0);
        } else {
          setLoyaltyCustomer(null);
        }
      } catch (_error) {
        // Cliente no encontrado en programa de fidelidad - no mostrar error
        setLoyaltyCustomer(null);
      }
      setLoadingLoyalty(false);
    } else {
      // Limpiar datos de fidelización si no hay cliente
      setLoyaltySearchName('');
      setLoyaltyCustomer(null);
      setPointsToRedeem(0);
      setLoyaltyDiscount(0);
    }
    
    setShowPaymentDialog(true);
  };

  // Split Payment Functions
  const addSplitPayment = () => {
    setSplitPayments([...splitPayments, { payment_method: 'cash', amount: '' }]);
  };

  const removeSplitPayment = (index) => {
    if (splitPayments.length > 1) {
      setSplitPayments(splitPayments.filter((_, i) => i !== index));
    }
  };

  const updateSplitPayment = (index, field, value) => {
    const updated = [...splitPayments];
    updated[index] = { ...updated[index], [field]: value };
    setSplitPayments(updated);
  };

  const calculateSplitTotal = () => {
    return splitPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  };

  const getRemainingAmount = () => {
    if (!selectedOrder) return 0;
    return selectedOrder.total - calculateSplitTotal();
  };

  const handleSplitPayment = async () => {
    if (!selectedOrder) return;
    
    // Solo el cajero puede procesar pagos
    if (user?.role !== 'cashier') {
      toast.error('Solo el cajero puede procesar pagos');
      return;
    }
    
    // Validate all payments have amounts
    const invalidPayments = splitPayments.filter(p => !p.amount || parseFloat(p.amount) <= 0);
    if (invalidPayments.length > 0) {
      toast.error('Todos los pagos deben tener un monto válido');
      return;
    }
    
    // Validate total matches
    const remaining = getRemainingAmount();
    if (Math.abs(remaining) > 0.01) {
      toast.error(`El total de pagos no coincide. ${remaining > 0 ? `Faltan $${formatAmount(remaining)}` : `Sobran $${formatAmount(Math.abs(remaining))}`}`);
      return;
    }

    setLoading(true);
    try {
      const payments = splitPayments.map(p => ({
        payment_method: p.payment_method,
        amount: parseFloat(p.amount)
      }));
      // Se captura la orden antes de limpiar el estado: es la que se imprime
      const orderForPrint = {
        ...selectedOrder,
        payment_method: payments.map(p => p.payment_method).join(' + ')
      };

      await axios.post(
        `${API}/payments/split`,
        {
          order_id: selectedOrder.id,
          payments: payments
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('¡Pago dividido procesado exitosamente!');
      // Ticket: primero la impresora del POS, si no hay se abre el diálogo
      await printReceiptWithFallback(orderForPrint);
      setShowPaymentDialog(false);
      setSelectedOrder(null);
      setIsSplitPayment(false);
      setSplitPayments([{ payment_method: 'cash', amount: '' }]);
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al procesar pago dividido'));
    }
    setLoading(false);
  };

  // Evalúa si la orden actual puede cobrarse a CRÉDITO: requiere un cliente
  // registrado (búsqueda de fidelidad) con credit_enabled y cupo suficiente.
  const getCreditInfo = () => {
    const c = creditCustomer || loyaltyCustomer;
    const total = selectedOrder
      ? (loyaltyDiscount > 0 ? selectedOrder.total - loyaltyDiscount : selectedOrder.total)
      : 0;
    const name = c ? (c.customer_name || c.name || c.customer_phone) : '';
    if (!c || !c.credit_enabled) {
      return {
        eligible: false,
        name,
        total,
        balance: 0,
        limit: 0,
        available: 0,
        reason: !c
          ? 'Selecciona un cliente registrado (búsqueda de fidelidad) para cobrar a crédito.'
          : 'El cliente no tiene crédito habilitado.'
      };
    }
    const limit = Number(c.credit_limit || 0);
    const balance = Number(c.credit_balance || 0);
    const available = limit > 0 ? Math.round((limit - balance) * 100) / 100 : Infinity;
    if (limit > 0 && total > available) {
      return {
        eligible: false, name, total, balance, limit, available,
        reason: `Excede el cupo de crédito. Disponible: $${formatAmount(available)}`
      };
    }
    return { eligible: true, name, total, balance, limit, available };
  };

  const handleProcessPayment = async () => {
    if (!selectedOrder) return;

    // Solo el cajero puede procesar pagos
    if (user?.role !== 'cashier') {
      toast.error('Solo el cajero puede procesar pagos');
      return;
    }

    setLoading(true);
    try {
      const finalTotal = loyaltyDiscount > 0 ? selectedOrder.total - loyaltyDiscount : selectedOrder.total;
      const posId = localStorage.getItem('pos_id')?.trim() || null;
      
      if (paymentMethod !== 'credit' && selectedOrder?.status === 'delivered' && selectedOrder?.payment_status === 'pending') {
        await handleMarkPaid(selectedOrder);
      } else {
        const paymentResponse = await axios.post(
          `${API}/payments`,
          {
            order_id: selectedOrder.id,
            payment_method: paymentMethod,
            amount: finalTotal,
            discount: loyaltyDiscount,
            loyalty_phone: loyaltyCustomer?.phone || null,
            pos_id: posId
          },
          { headers: getAuthHeaders() }
        );
        // /payments ya intentó el ticket en la impresora del POS; si no lo
        // confirmó se abre el diálogo del navegador para no perder la venta
        const autoPrinted = paymentResponse.data?.pos_print?.status === 'sent';
        if (autoPrinted) {
          toast.success('Ticket enviado a la impresora del POS');
        } else {
          const orderForPrint = {
            ...selectedOrder,
            payment_method: paymentMethod,
            loyalty_discount: loyaltyDiscount,
            final_total: finalTotal
          };
          printReceipt(orderForPrint);
        }
        toast.success('Pago procesado exitosamente');
      }
      
      setShowPaymentDialog(false);
      setSelectedOrder(null);
      clearLoyaltyData();
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al procesar pago'));
    }
    setLoading(false);
  };

  const handleMarkPaid = async (order) => {
    // Solo el cajero puede marcar pagos
    if (user?.role !== 'cashier') {
      toast.error('Solo el cajero puede procesar pagos');
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/orders/${order.id}/mark-paid`,
        null,
        {
          params: { payment_method: paymentMethod },
          headers: getAuthHeaders()
        }
      );
      
      // Ticket de la orden COD marcada como pagada: impresora del POS o diálogo
      const orderForPrint = {
        ...order,
        payment_method: paymentMethod,
      };
      await printReceiptWithFallback(orderForPrint);

      toast.success('¡Pago registrado exitosamente!');
      setShowPaymentDialog(false);
      setSelectedOrder(null);
      loadOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al registrar pago'));
    }
    setLoading(false);
  };

  const handleSendInvoice = async () => {
    if (!selectedOrder || !invoiceEmail) {
      toast.error("Ingresa un correo electrónico");
      return;
    }
    if (!invoiceEmail.includes("@") || !invoiceEmail.includes(".")) {
      toast.error("Ingresa un correo electrónico válido");
      return;
    }
    
    setSendingInvoice(true);
    try {
      const response = await axios.post(
        `${API}/orders/${selectedOrder.id}/send-invoice`,
        null,
        {
          params: { email: invoiceEmail },
          headers: getAuthHeaders()
        }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || "Error al enviar la factura";
      toast.error(detail);
    }
    setSendingInvoice(false);
  };

  const getStatusBadge = (status, paymentStatus) => {
    // Show payment status badges for delivered orders
    if (status === 'delivered' && paymentStatus === 'paid') {
      return <Badge className="bg-green-100 text-green-700">✓ Pagado</Badge>;
    }
    if (status === 'delivered' && paymentStatus === 'pending') {
      return <Badge className="bg-orange-100 text-orange-700">Pendiente de Pago</Badge>;
    }
    
    const statusMap = {
      pending: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-700' },
      in_preparation: { label: 'En Preparación', className: 'bg-blue-100 text-blue-700' },
      ready: { label: 'Listo', className: 'bg-green-100 text-green-700' },
    };
    const statusInfo = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-700' };
    return <Badge className={statusInfo.className}>{statusInfo.label}</Badge>;
  };

  const getOrderTypeLabel = (type) => {
    const types = {
      dine_in: 'En Local',
      takeout: 'Para Llevar',
      delivery: 'Delivery'
    };
    return types[type] || type;
  };

  // El bloqueo por estado "Listo" solo aplica a los productos que se preparan en
  // cocina (print_station === 'kitchen', lo mismo que muestra el KDS). Una orden
  // compuesta solo por productos de bar/postres/parrilla se puede cobrar de
  // inmediato, sin esperar a que la cocina la marque como lista.
  // Si un item no tiene categoría resuelta, se asume cocina (comportamiento viejo).
  const requiresKitchenReady = (order) => {
    if (categories.length === 0) return true;
    const items = order?.items || [];
    if (items.length === 0) return true;
    return items.some(item => {
      const category = categories.find(c => c.id === item.category_id);
      if (!category) return true;
      return (category.print_station || 'kitchen') === 'kitchen';
    });
  };

  const filteredOrders = orders.filter(order => {
    const search = searchQuery.toLowerCase();
    const matchesSearch = (
      order.order_number?.toString().includes(search) ||
      order.customer_name?.toLowerCase().includes(search) ||
      order.customer_phone?.includes(search)
    );
    const matchesType = orderTypeFilter === 'all' || order.order_type === orderTypeFilter;
    return matchesSearch && matchesType;
  });

  const totalToCollect = filteredOrders.reduce((sum, order) => sum + (order.total || 0), 0);

  // Preview del cuadre: lo declarado vs lo que registró el sistema
  const declaredPreview = (parseFloat(closingData.declared_cash) || 0) +
    (parseFloat(closingData.declared_card) || 0) +
    (parseFloat(closingData.declared_transfer) || 0);
  const previewDifference = declaredPreview - (cashSummary?.system_total || 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-orange-100 sticky top-0 z-50">
        <div className="max-w-full mx-auto px-2 sm:px-4 py-2 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile Menu - Solo visible en móvil */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="md:hidden p-2 border-orange-200">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] sm:w-[350px]">
                <SheetHeader>
                  <SheetTitle>Menú</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-4">
                  <div className="pb-4 border-b">
                    <p className="text-sm text-gray-500">Usuario</p>
                    <p className="font-semibold">{user?.name}</p>
                    <p className="text-xs text-gray-400">{user?.email}</p>
                  </div>
                  
                  {cashSession && (
                    <Button 
                      variant="outline" 
                      onClick={() => setShowOrderHistory(true)}
                      className="w-full justify-start border-orange-200 hover:bg-orange-50"
                    >
                      <History className="w-4 h-4 mr-2" />
                      Historial de Órdenes
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    onClick={() => navigate('/profile')}
                    className="w-full justify-start"
                  >
                    <User className="w-4 h-4 mr-2" />
                    Mi Perfil
                  </Button>
                  
                  <Button
                    variant="destructive"
                    onClick={logout}
                    disabled={isLoggingOut}
                    className="w-full justify-start"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    {isLoggingOut ? 'Cerrando sesión...' : 'Cerrar Sesión'}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
              <Receipt className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                POS
              </h1>
              <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">{user?.name}</p>
            </div>
            {/* Offline Indicator */}
            {isOffline && (
              <Badge variant="destructive" className="ml-1 sm:ml-2 animate-pulse flex items-center gap-1 text-xs">
                <WifiOff className="w-3 h-3" />
                <span className="hidden sm:inline">Offline</span>
              </Badge>
            )}
            {isOnline && pendingActions > 0 && (
              <Badge className="ml-1 sm:ml-2 bg-yellow-100 text-yellow-700 hover:bg-yellow-200 cursor-pointer text-xs" onClick={handleSyncOfflineOrders}>
                {pendingActions} <span className="hidden sm:inline">pendiente(s)</span>
              </Badge>
            )}
            {/* POS ID Badge */}
            <button
              onClick={() => { setPosIdInput(posId); setShowPosIdDialog(true); }}
              title="Configurar ID de caja para impresión automática"
              className="ml-1 sm:ml-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-dashed border-gray-300 text-gray-500 hover:border-orange-400 hover:text-orange-600 transition-colors"
            >
              <Printer className="w-3 h-3" />
              <span className="hidden sm:inline">{posId ? posId : 'Sin ID caja'}</span>
            </button>
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Order History Button - Solo desktop */}
            {cashSession && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowOrderHistory(true)}
                className="hidden md:flex border-orange-200 hover:bg-orange-50 rounded-xl p-2 sm:px-4"
              >
                <History className="w-4 h-4" />
                <span className="hidden md:inline ml-2">Historial</span>
              </Button>
            )}
            
            {/* User Menu - Solo desktop */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="hidden md:flex border-orange-200 hover:bg-orange-50 rounded-xl p-2 sm:px-4">
                  <User className="w-4 h-4" />
                  <span className="hidden md:inline ml-2">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Mi Cuenta</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')}>
                  <User className="w-4 h-4 mr-2" />
                  Perfil
                </DropdownMenuItem>
                {cashSession && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={openCloseCashDialog}>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Cerrar Caja
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Salir
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Alerta de Caja Sin Cerrar */}
      {unclosedRegister && (
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-2xl border-b-4 border-red-800 animate-pulse">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-white/20 p-3 rounded-full animate-bounce">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-2xl font-bold mb-1">
                    ⚠️ {unclosedRegister.message}
                  </p>
                  <p className="text-sm opacity-90">
                    Debes cerrar la caja del día {new Date(unclosedRegister.date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })} antes de abrir una nueva
                  </p>
                </div>
              </div>
              <Button 
                onClick={openCloseCashDialog}
                className="bg-white text-red-600 hover:bg-gray-100 font-bold px-8 py-6 text-lg rounded-xl shadow-xl"
              >
                Cerrar Caja Ahora
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={`max-w-full mx-auto px-2 sm:px-4 py-2 sm:py-4 grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6 ${!cashSession ? 'opacity-50 pointer-events-none' : ''}`}>
        {/* Left: Products & Cart */}
        <div className="lg:col-span-2 space-y-3 sm:space-y-4">
          {/* Cart */}
          <Card className="p-3 sm:p-4 bg-white/80 backdrop-blur-xl border-orange-100">
            <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Carrito Actual</h2>
            
            {cart.length === 0 ? (
              <p className="text-gray-500 text-center py-4 text-sm">Carrito vacío</p>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {cart.map(item => (
                  <div key={`${item.id}-${item.variant_id || 'no-variant'}`} className="flex flex-col gap-2 bg-white p-2 sm:p-3 rounded-xl shadow-sm">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm sm:text-base truncate">{item.name}</p>
                        {item.variant_name && (
                          <p className="text-xs text-orange-600 font-medium">{item.variant_name}</p>
                        )}
                        <p className="text-xs sm:text-sm text-gray-600">${formatAmount(item.price)}</p>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateCartQuantity(item.id, item.quantity - 1, item.variant_id)}
                          className="h-7 w-7 sm:h-8 sm:w-8 p-0 rounded-lg text-xs sm:text-base"
                        >
                          -
                        </Button>
                        <span className="w-6 sm:w-8 text-center font-medium text-sm">{item.quantity}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateCartQuantity(item.id, item.quantity + 1, item.variant_id)}
                          className="h-7 w-7 sm:h-8 sm:w-8 p-0 rounded-lg text-xs sm:text-base"
                        >
                          +
                        </Button>
                      </div>
                      <p className="font-bold text-orange-600 text-sm sm:text-base flex-shrink-0">${formatAmount(item.price * item.quantity)}</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeFromCart(item.id, item.variant_id)}
                        className="h-8 w-8 p-0 rounded-lg text-red-500 hover:text-red-700 hover:bg-red-50"
                        title="Eliminar producto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* Notas del producto */}
                    <Input
                      placeholder="Notas (ej: sin cebolla, bien cocido...)"
                      value={item.notes || ''}
                      onChange={(e) => updateCartItemNotes(item.id, e.target.value, item.variant_id)}
                      className="rounded-lg text-sm bg-gray-50 border-gray-200"
                    />
                  </div>
                ))}
                <div className="border-t pt-3">
                  {(() => {
                    const t = calcTotals(calculateTotal(), tenantInfo);
                    return (
                      <>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-600">Subtotal:</span>
                          <span className="font-semibold">${formatAmount(t.subtotal)}</span>
                        </div>
                        {t.tax > 0 && (
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{itbisLabel()}:</span>
                            <span className="font-semibold">${formatAmount(t.tax)}</span>
                          </div>
                        )}
                        <div className="flex justify-between items-center">
                          <span className="font-bold">Total:</span>
                          <span className="text-2xl font-bold text-orange-600">${formatAmount(t.total)}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
                
                <div className="space-y-3 pt-3 border-t">
                  <Select value={orderType} onValueChange={(value) => {
                    setOrderType(value);
                    if (value !== 'dine_in') setSelectedTable('');
                  }}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dine_in">En Local</SelectItem>
                      <SelectItem value="takeout">Para Llevar</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {orderType === 'dine_in' && (
                    <div>
                      <Label className="text-sm text-gray-600 mb-1 block">Mesa *</Label>
                      <Select value={selectedTable} onValueChange={setSelectedTable}>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Selecciona una mesa" />
                        </SelectTrigger>
                        <SelectContent>
                          {tables
                            .filter(table => table.status === 'free')
                            .map(table => (
                              <SelectItem key={table.id} value={table.id}>
                                {table.name || `Mesa ${table.number}`} (Cap: {table.capacity})
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      {tables.filter(t => t.status === 'free').length === 0 && (
                        <p className="text-xs text-red-600 mt-1">
                          No hay mesas disponibles
                        </p>
                      )}
                    </div>
                  )}
                  
                  {orderType === 'dine_in' && (
                    <div>
                      <Label className="text-sm text-gray-600 mb-1 block">Mesero *</Label>
                      <Select value={selectedWaiter} onValueChange={setSelectedWaiter}>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Selecciona un mesero" />
                        </SelectTrigger>
                        <SelectContent>
                          {waiters.map(waiter => (
                            <SelectItem key={waiter.id} value={waiter.id}>
                              {waiter.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {waiters.length === 0 && (
                        <p className="text-xs text-red-600 mt-1">
                          No hay meseros disponibles
                        </p>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <Label className="flex items-center gap-1 mb-2">
                      Cliente <span className="text-red-500">*</span>
                      {!selectedCustomer && isGenericCustomerName(customerName) && (
                        <Badge variant="outline" className="ml-auto text-[10px] font-normal border-amber-300 text-amber-700 bg-amber-50">
                          Sin registrar
                        </Badge>
                      )}
                    </Label>
                    {!selectedCustomer ? (
                      <div className="relative">
                        <Input
                          placeholder="Buscar por nombre..."
                          value={customerName}
                          onChange={(e) => {
                            setCustomerName(e.target.value);
                            // Abrir dropdown automáticamente cuando hay 2+ caracteres,
                            // salvo que siga intacto el nombre genérico por defecto
                            if (e.target.value.trim().length >= 2 && !isGenericCustomerName(e.target.value)) {
                              setShowCustomerDropdown(true);
                            } else {
                              setShowCustomerDropdown(false);
                            }
                          }}
                          onFocus={(e) => {
                            // Si trae el default, se selecciona entero: el cajero empieza
                            // a teclear y lo reemplaza sin tener que borrar a mano
                            if (isGenericCustomerName(customerName)) {
                              e.target.select();
                              return;
                            }
                            if (customerName.trim().length >= 2) {
                              setShowCustomerDropdown(true);
                            }
                          }}
                          className="rounded-xl flex-1"
                          required
                        />
                        {showCustomerDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-56 overflow-y-auto">
                            {customerSearchResults
                              .filter(c => c.name.toLowerCase().includes(customerName.trim().toLowerCase()))
                              .slice(0, 10)
                              .map((customer) => (
                                <div
                                  key={customer.id}
                                  onClick={() => selectCustomer(customer)}
                                  className="p-3 hover:bg-orange-50 cursor-pointer border-b last:border-b-0 transition-colors"
                                >
                                  <p className="font-semibold text-sm text-gray-800">{customer.name}</p>
                                  {customer.phone && (
                                    <p className="text-xs text-gray-500">{customer.phone}</p>
                                  )}
                                  {customer.loyalty_points > 0 && (
                                    <p className="text-xs text-green-600">Puntos: {customer.loyalty_points}</p>
                                  )}
                                </div>
                              ))}
                            {customerSearchResults.filter(c => 
                              c.name.toLowerCase().includes(customerName.trim().toLowerCase())
                            ).length === 0 && (
                              <div className="p-4 text-center">
                                <p className="text-gray-500 text-sm mb-3">No se encontró el cliente</p>
                                <Button
                                  type="button"
                                  onClick={openCreateCustomerDialog}
                                  size="sm"
                                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl"
                                >
                                  + Crear Cliente
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                        <div className="flex-1">
                          <p className="font-semibold text-sm">{selectedCustomer.name}</p>
                          {selectedCustomer.phone && (
                            <p className="text-xs text-gray-600">{selectedCustomer.phone}</p>
                          )}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={clearSelectedCustomer}
                          className="text-red-600 hover:bg-red-50 rounded-xl"
                        >
                          Cambiar
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {editingOrder && (
                    <Button
                      onClick={handleCancelEdit}
                      variant="outline"
                      className="w-full border-gray-300 hover:bg-gray-50 rounded-xl mb-2"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancelar Edición
                    </Button>
                  )}
                  
                  <Button
                    onClick={handleCreateOrder}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {editingOrder ? 'Actualizar Orden' : 'Crear Orden'}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Products */}
          <Card className="p-3 sm:p-4 bg-white/80 backdrop-blur-xl border-orange-100">
            {/* Buscador de productos */}
            <div className="mb-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar producto o código de barras..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="pl-10 rounded-xl text-sm"
                />
              </div>
            </div>
            
            <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">Productos</h2>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2 sm:gap-3">
              {products.filter(p => {
                // Filtrar productos disponibles
                if (p.is_available === false) return false;

                // Ocultar productos con inventario agotado (gestionados y sin existencia)
                if (p.tracked && (p.stock ?? 1) <= 0) return false;
                
                // Filtrar por búsqueda (nombre, descripción o código de barras)
                if (productSearch.trim()) {
                  const search = productSearch.toLowerCase();
                  const matchesName = p.name.toLowerCase().includes(search);
                  const matchesDescription = p.description?.toLowerCase().includes(search);
                  const matchesBarcode = (p.barcode || '').toLowerCase().includes(search);
                  if (!matchesName && !matchesDescription && !matchesBarcode) return false;
                }
                
                // Filtrar categoría "Delivery" según tipo de orden
                const deliveryCategory = categories.find(c => c.name.toLowerCase() === 'delivery');
                if (deliveryCategory && p.category_id === deliveryCategory.id) {
                  // Solo mostrar productos de delivery si el tipo de orden es delivery
                  return orderType === 'delivery';
                }
                
                return true;
              }).map(product => (
                <Card
                  key={product.id}
                  className="cursor-pointer hover:shadow-lg transition-all active:scale-95 overflow-hidden touch-manipulation"
                  onClick={() => addToCart(product)}
                >
                  {/* Product Image - Offline Ready */}
                  <div className="relative h-28 sm:h-36 bg-gradient-to-br from-orange-100 to-red-100 overflow-hidden">
                    <OfflineImage
                      src={getImageUrl(product.image_url)}
                      alt={product.name}
                      className="w-full h-full object-contain"
                      type="product"
                      fallbackIcon={Package}
                    />
                  </div>
                  
                  {/* Product Info */}
                  <div className="p-2 sm:p-3">
                    <h3 className="font-medium text-xs sm:text-sm line-clamp-2 mb-1">{product.name}</h3>
                    {product.variants && product.variants.length > 0 ? (
                      <div className="space-y-1">
                        {product.variants.filter(v => v.is_available).slice(0, 3).map((variant) => (
                          <div key={variant.id} className="flex justify-between items-center text-xs">
                            <span className="text-gray-700 truncate flex-1">{variant.name}</span>
                            <span className="font-semibold text-orange-600 ml-1">${formatAmount(variant.price)}</span>
                          </div>
                        ))}
                        {product.variants.filter(v => v.is_available).length > 3 && (
                          <p className="text-xs text-gray-500 italic">+{product.variants.filter(v => v.is_available).length - 3} más</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm sm:text-lg font-bold text-orange-600">${formatAmount(product.price)}</p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </Card>
        </div>

        {/* Right: Orders */}
        <div className="space-y-3 sm:space-y-4">
          <Card className="p-3 sm:p-4 bg-white/80 backdrop-blur-xl border-orange-100">
            <div className="flex justify-between items-center mb-3 sm:mb-4">
              <h2 className="text-base sm:text-lg font-bold">Órdenes</h2>
              <div className="text-right">
                <p className="text-xs sm:text-sm text-gray-600">Pendientes</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-600">{filteredOrders.length}</p>
              </div>
            </div>
            
            <div className="mb-3 sm:mb-4 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar orden..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-xl text-sm"
                />
              </div>
              <Select value={orderTypeFilter} onValueChange={setOrderTypeFilter}>
                <SelectTrigger className="rounded-xl text-sm">
                  <SelectValue placeholder="Filtrar por tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las órdenes</SelectItem>
                  <SelectItem value="dine_in">En Local</SelectItem>
                  <SelectItem value="takeout">Para Llevar</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
              <div className="bg-gradient-to-r from-orange-100 to-red-100 p-2 sm:p-3 rounded-xl">
                <p className="text-xs sm:text-sm text-gray-700">Total a Facturar</p>
                <p className="text-xl sm:text-2xl font-bold text-orange-700">${formatAmount(totalToCollect)}</p>
              </div>
            </div>
          </Card>

          <div className="space-y-2 sm:space-y-3 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
            {filteredOrders.map(order => (
              <Card key={order.id} className="p-3 sm:p-4 bg-white/80 backdrop-blur-xl border-orange-100">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold">Orden #{order.order_number}</h3>
                      {order.table_id && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {tables.find(t => t.id === order.table_id)?.name || `Mesa ${tables.find(t => t.id === order.table_id)?.number || ''}`}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{order.customer_name}</p>
                    <p className="text-xs text-gray-500">{getOrderTypeLabel(order.order_type)}</p>
                  </div>
                  {getStatusBadge(order.status, order.payment_status)}
                </div>
                
                <div className="space-y-1 mb-3">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span>{item.quantity}x {item.product_name}</span>
                      <span>${formatAmount(item.subtotal)}</span>
                    </div>
                  ))}
                </div>
                
                <div className="border-t pt-2 flex justify-between items-center mb-3">
                  <span className="font-bold">Total:</span>
                  <span className="font-bold text-orange-600">${formatAmount(order.total)}</span>
                </div>

                {/* Transfer Proof Indicator */}
                {order.transfer_proof_url && (
                  <div 
                    onClick={() => {
                      setCurrentTransferProof(order.transfer_proof_url);
                      setShowTransferProof(true);
                    }}
                    className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 cursor-pointer hover:bg-blue-100 transition-colors"
                  >
                    <Image className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-700 font-medium">Comprobante de transferencia adjunto</span>
                    <span className="text-xs text-blue-500 ml-auto">Click para ver</span>
                  </div>
                )}

                <div className="flex gap-2">
                  {/* Botón de editar para órdenes pendientes tipo takeout */}
                  {order.status === 'pending' && order.order_type === 'takeout' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditOrder(order)}
                      title="Editar Orden"
                      className="border-blue-200 hover:bg-blue-50 rounded-xl"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => printComandaHTML(order)}
                    title="Imprimir Comanda"
                    className="border-orange-200 hover:bg-orange-50 rounded-xl"
                  >
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => printReceipt(order)}
                    title="Imprimir Recibo"
                    className="border-green-200 hover:bg-green-50 rounded-xl"
                  >
                    <Receipt className="w-4 h-4" />
                  </Button>
                  {/* Solo mostrar botón de pago para cajeros */}
                  {user?.role === 'cashier' && (
                    <Button
                      onClick={() => handleOpenPayment(order)}
                      disabled={
                        order.status === 'cancelled' || 
                        (order.status === 'delivered' && order.payment_status !== 'pending') ||
                        // Solo las órdenes con productos de cocina exigen estado ready o delivered;
                        // las de bar se habilitan desde que se crean
                        (requiresKitchenReady(order) && order.status !== 'ready' && order.status !== 'delivered')
                      }
                      data-testid={`pay-order-${order.id}`}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      {order.status === 'delivered' && order.payment_status === 'pending' ? 'Registrar Pago' : 'Pagar'}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {filteredOrders.length === 0 && (
            <div className="text-center py-12">
              <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchQuery ? 'No se encontraron órdenes' : 'No hay órdenes pendientes de pago'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Procesar Pago
            </DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Total */}
              <div className="text-center py-4 bg-orange-50 rounded-xl">
                <p className="text-sm text-gray-600">Orden #{selectedOrder.order_number}</p>
                {loyaltyDiscount > 0 ? (
                  <>
                    <p className="text-lg text-gray-400 line-through">${formatAmount(selectedOrder.total)}</p>
                    <p className="text-sm text-purple-600">-${formatAmount(loyaltyDiscount)} (Puntos)</p>
                    <p className="text-3xl font-bold text-green-600">${(selectedOrder.total - formatAmount(loyaltyDiscount))}</p>
                  </>
                ) : (
                  <p className="text-3xl font-bold text-orange-600">${formatAmount(selectedOrder.total)}</p>
                )}
              </div>

              {/* Transfer Proof Preview in Payment Dialog */}
              {selectedOrder.transfer_proof_url && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Image className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-blue-800">Comprobante de Transferencia</span>
                  </div>
                  <div 
                    onClick={() => {
                      setCurrentTransferProof(selectedOrder.transfer_proof_url);
                      setShowTransferProof(true);
                    }}
                    className="relative rounded-lg overflow-hidden border border-blue-200 cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    <img 
                      src={selectedOrder.transfer_proof_url} 
                      alt="Comprobante"
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <span className="text-white font-medium text-sm">Click para ampliar</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Loyalty Points Redemption */}
              <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl space-y-3">
                <div className="flex items-center gap-2">
                  <Gift className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-800">Puntos de Fidelidad</span>
                </div>
                
                {loadingLoyalty ? (
                  <div className="text-center py-2">
                    <p className="text-sm text-purple-600">Cargando puntos...</p>
                  </div>
                ) : !loyaltyCustomer ? (
                  <div className="text-center py-2">
                    <p className="text-sm text-gray-600">
                      {selectedOrder?.customer_name 
                        ? 'Cliente sin puntos acumulados' 
                        : 'Orden sin cliente asociado'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-2 bg-white rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-800">
                          {loyaltyCustomer.customer_name || loyaltyCustomer.name || 'Cliente'}
                        </p>
                        <p className="text-sm text-gray-600">
                          {loyaltyCustomer.customer_phone || loyaltyCustomer.phone || selectedOrder?.customer_phone}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">Puntos disponibles</p>
                        <p className="text-xl font-bold text-purple-600">
                          {loyaltyCustomer.total_points || 0}
                        </p>
                      </div>
                    </div>
                    
                    {loyaltyCustomer.total_points >= (loyaltyConfig?.min_points_redemption || 100) ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <Label className="text-xs">Puntos a canjear</Label>
                            <Input
                              type="number"
                              value={pointsToRedeem || ''}
                              onChange={(e) => handlePointsChange(e.target.value)}
                              placeholder={`Mín: ${loyaltyConfig?.min_points_redemption || 100}`}
                              className="rounded-xl"
                              min={0}
                              max={loyaltyCustomer.total_points}
                            />
                          </div>
                          <div className="flex-1">
                            <Label className="text-xs">Descuento</Label>
                            <div className="h-10 px-3 flex items-center bg-green-100 rounded-xl font-bold text-green-700">
                              ${formatAmount(loyaltyDiscount)}
                            </div>
                          </div>
                        </div>
                        
                        {loyaltyDiscount === 0 && (
                          <Button
                            type="button"
                            onClick={applyLoyaltyDiscount}
                            disabled={pointsToRedeem < (loyaltyConfig?.min_points_redemption || 100)}
                            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl"
                          >
                            Aplicar Descuento
                          </Button>
                        )}
                        
                        {loyaltyDiscount > 0 && (
                          <div className="flex items-center gap-2 p-2 bg-green-100 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="text-green-700 font-medium">
                              Descuento de ${formatAmount(loyaltyDiscount)} aplicado
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-amber-600 text-center">
                        Necesita mínimo {loyaltyConfig?.min_points_redemption || 100} puntos para canjear
                      </p>
                    )}
                    

                  </div>
                )}
              </div>

              {/* Split Payment Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2">
                  <Split className="w-5 h-5 text-orange-500" />
                  <span className="font-medium">Dividir Pago</span>
                </div>
                <Switch 
                  checked={isSplitPayment}
                  onCheckedChange={(checked) => {
                    setIsSplitPayment(checked);
                    if (checked) {
                      setSplitPayments([{ payment_method: 'cash', amount: '' }]);
                    }
                  }}
                />
              </div>

              {/* Simple Payment Mode */}
              {!isSplitPayment && (
                <div className="space-y-3">
                  <div>
                    <Label>Método de Pago</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger className="rounded-xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">
                          <div className="flex items-center gap-2">
                            <Banknote className="w-4 h-4" />
                            Efectivo
                          </div>
                        </SelectItem>
                        <SelectItem value="card">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            Tarjeta
                          </div>
                        </SelectItem>
                        <SelectItem value="transfer">
                          <div className="flex items-center gap-2">
                            <Wallet className="w-4 h-4" />
                            Transferencia
                          </div>
                        </SelectItem>
                        <SelectItem value="credit">
                          <div className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            Crédito
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Credit gating: requires a registered customer with credit enabled */}
                  {paymentMethod === 'credit' && (() => {
                    const ci = getCreditInfo();
                    return (
                      <div className={`p-4 border rounded-xl ${ci.eligible ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCard className="w-5 h-5" />
                          <span className="font-semibold">Cobro a Crédito</span>
                        </div>
                        {ci.eligible ? (
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between"><span className="text-gray-600">Cliente:</span><span className="font-medium">{ci.name}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Saldo actual:</span><span className="font-medium">${formatAmount(ci.balance)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Cupo:</span><span className="font-medium">{ci.limit > 0 ? '$' + formatAmount(ci.limit) : 'Sin límite'}</span></div>
                            <div className="flex justify-between"><span className="text-gray-600">Disponible:</span><span className="font-medium">{ci.available === Infinity ? 'Sin límite' : '$' + formatAmount(ci.available)}</span></div>
                            <div className="flex justify-between border-t pt-1"><span className="font-semibold">Saldo tras esta venta:</span><span className="font-bold">${formatAmount(Math.round((ci.balance + ci.total) * 100) / 100)}</span></div>
                          </div>
                        ) : (
                          <p className="text-sm text-red-700">{ci.reason}</p>
                        )}
                      </div>
                    );
                  })()}
                  
                  {/* Bank Info for Transfer Payment */}
                  {paymentMethod === 'transfer' && tenantInfo && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Wallet className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-800">Datos para Transferencia</span>
                      </div>
                      {tenantInfo.bank_name && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Banco:</span>
                          <span className="font-medium">{tenantInfo.bank_name}</span>
                        </div>
                      )}
                      {tenantInfo.bank_account_type && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tipo de Cuenta:</span>
                          <span className="font-medium">{tenantInfo.bank_account_type}</span>
                        </div>
                      )}
                      {tenantInfo.bank_account && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Número de Cuenta:</span>
                          <span className="font-medium font-mono">{tenantInfo.bank_account}</span>
                        </div>
                      )}
                      {tenantInfo.bank_account_holder && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Titular:</span>
                          <span className="font-medium">{tenantInfo.bank_account_holder}</span>
                        </div>
                      )}
                      {!tenantInfo.bank_account && !tenantInfo.bank_account_holder && (
                        <p className="text-xs text-amber-600 mt-2">
                          Los datos bancarios no están configurados. Configure los datos en el panel de administración.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Split Payment Mode */}
              {isSplitPayment && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-semibold">Pagos Divididos</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addSplitPayment}
                      className="rounded-lg border-orange-200 text-orange-600 hover:bg-orange-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>

                  {/* Split Payments List */}
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {splitPayments.map((split, index) => (
                      <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl">
                        <div className="flex-1">
                          <Select 
                            value={split.payment_method} 
                            onValueChange={(value) => updateSplitPayment(index, 'payment_method', value)}
                          >
                            <SelectTrigger className="rounded-lg h-9">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="cash">Efectivo</SelectItem>
                              <SelectItem value="card">Tarjeta</SelectItem>
                              <SelectItem value="transfer">Transferencia</SelectItem>
                              <SelectItem value="credit">Crédito</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-28">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="$0.00"
                            value={split.amount}
                            onChange={(e) => updateSplitPayment(index, 'amount', e.target.value)}
                            className="rounded-lg h-9 text-right"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeSplitPayment(index)}
                          disabled={splitPayments.length === 1}
                          className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>

                  {/* Split Summary */}
                  <div className="p-3 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total de la Orden:</span>
                      <span className="font-semibold">${formatAmount(selectedOrder.total)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Pagado:</span>
                      <span className="font-semibold text-green-600">${formatAmount(calculateSplitTotal())}</span>
                    </div>
                    <div className="border-t border-orange-200 pt-2 flex justify-between">
                      <span className="font-semibold">Restante:</span>
                      <span className={`font-bold text-lg ${getRemainingAmount() === 0 ? 'text-green-600' : getRemainingAmount() > 0 ? 'text-orange-600' : 'text-red-600'}`}>
                        ${formatAmount(getRemainingAmount())}
                      </span>
                    </div>
                  </div>

                  {/* Bank Info for Split Payment with Transfer */}
                  {splitPayments.some(p => p.payment_method === 'transfer') && tenantInfo && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl space-y-2">
                      <div className="flex items-center gap-2 mb-2">
                        <Wallet className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-800">Datos para Transferencia</span>
                      </div>
                      {tenantInfo.bank_name && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Banco:</span>
                          <span className="font-medium">{tenantInfo.bank_name}</span>
                        </div>
                      )}
                      {tenantInfo.bank_account_type && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tipo de Cuenta:</span>
                          <span className="font-medium">{tenantInfo.bank_account_type}</span>
                        </div>
                      )}
                      {tenantInfo.bank_account && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Número de Cuenta:</span>
                          <span className="font-medium font-mono">{tenantInfo.bank_account}</span>
                        </div>
                      )}
                      {tenantInfo.bank_account_holder && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Titular:</span>
                          <span className="font-medium">{tenantInfo.bank_account_holder}</span>
                        </div>
                      )}
                      {!tenantInfo.bank_account && !tenantInfo.bank_account_holder && (
                        <p className="text-xs text-amber-600 mt-2">
                          Los datos bancarios no están configurados. Configure los datos en el panel de administración.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Invoice Email */}
              <div className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-800">Enviar Factura por Email</span>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="correo@cliente.com"
                    value={invoiceEmail}
                    onChange={(e) => setInvoiceEmail(e.target.value)}
                    className="rounded-xl flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleSendInvoice}
                    disabled={sendingInvoice || !invoiceEmail}
                    className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white rounded-xl"
                  >
                    <Send className="w-4 h-4 mr-1" />
                    {sendingInvoice ? 'Enviando...' : 'Enviar'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  La factura se enviará con el detalle completo de la orden.
                  Requiere configuración SMTP en el restaurante.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPaymentDialog(false);
                    setIsSplitPayment(false);
                    setSplitPayments([{ payment_method: 'cash', amount: '' }]);
                  }}
                  className="flex-1 rounded-xl"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    if (isSplitPayment) {
                      handleSplitPayment();
                    } else if (paymentMethod !== 'credit' && selectedOrder?.status === 'delivered' && selectedOrder?.payment_status === 'pending') {
                      handleMarkPaid(selectedOrder);
                    } else {
                      handleProcessPayment();
                    }
                  }}
                  disabled={loading || (isSplitPayment && Math.abs(getRemainingAmount()) > 0.01) || (!isSplitPayment && paymentMethod === 'credit' && !getCreditInfo().eligible)}
                  data-testid="confirm-payment-button"
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl disabled:opacity-50"
                >
                  {loading ? 'Procesando...' : isSplitPayment ? 'Confirmar Pago Dividido' : 'Confirmar Pago'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Abrir Caja */}
      <Dialog open={showOpenCashDialog} onOpenChange={() => {}}>
        <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">Abrir Caja Registradora</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-xl text-center">
              <p className="text-sm text-gray-600 mb-2">Bienvenido, {user?.name}</p>
              <p className="text-xs text-gray-500">Debes abrir la caja antes de empezar a trabajar</p>
            </div>
            
            <div>
              <Label>Monto Inicial en Efectivo</Label>
              <Input
                type="number"
                step="0.01"
                value={openingAmount}
                onChange={(e) => setOpeningAmount(e.target.value)}
                placeholder="Ej: 500.00"
                className="rounded-xl text-lg"
                autoFocus
              />
              <p className="text-xs text-gray-500 mt-1">Ingresa el monto de efectivo con el que inicias</p>
            </div>
            
            <Button
              onClick={handleOpenCash}
              disabled={loading || !openingAmount}
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-xl h-12 text-lg"
            >
              {loading ? 'Abriendo...' : 'Abrir Caja'}
            </Button>

            {/* Salida alternativa: el cajero puede cerrar sesión sin abrir caja
                (tras un cierre de caja, o si entró con la cuenta equivocada).
                Sin esto, el modal forzado lo deja atrapado sin poder salir. */}
            <Button
              variant="outline"
              onClick={logout}
              disabled={isLoggingOut || loading}
              className="w-full rounded-xl h-11 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {isLoggingOut ? 'Cerrando sesión...' : 'Cerrar Sesión'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Cerrar Caja */}
      <Dialog open={showCloseCashDialog} onOpenChange={setShowCloseCashDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">Cerrar Caja Registradora</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {cashSession && (
              <div className="bg-blue-50 p-4 rounded-xl">
                <p className="text-sm text-gray-600 mb-2">Sesión iniciada:</p>
                <p className="text-xs text-gray-500">
                  {new Date(cashSession.opening_time).toLocaleString('es-ES')}
                </p>
                <p className="text-sm font-semibold mt-2">
                  Monto inicial: ${formatAmount(cashSession.opening_amount) || '0.00'}
                </p>
              </div>
            )}

            {/* Lo que vendió el sistema en esta caja: base del cuadre */}
            <div className="bg-white border-2 border-blue-200 p-4 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-blue-700 flex items-center gap-2">
                  <Receipt className="w-4 h-4" />
                  Ventas del Sistema
                </p>
                <button
                  type="button"
                  onClick={loadCashSummary}
                  disabled={loadingCashSummary}
                  className="text-xs text-blue-600 underline disabled:opacity-50"
                >
                  {loadingCashSummary ? 'Actualizando...' : 'Actualizar'}
                </button>
              </div>
              {cashSummary ? (
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Efectivo:</span>
                    <span className="font-medium">${formatAmount(cashSummary.system_cash)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tarjeta:</span>
                    <span className="font-medium">${formatAmount(cashSummary.system_card)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Transferencia:</span>
                    <span className="font-medium">${formatAmount(cashSummary.system_transfer)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1 mt-1 font-bold">
                    <span>Total:</span>
                    <span className="text-blue-600">${formatAmount(cashSummary.system_total)}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {cashSummary.tickets_count} cobro(s) registrado(s) · Efectivo esperado en el cajón: $
                    {formatAmount(cashSummary.expected_cash)}
                  </p>
                  {(cashSummary.system_total || 0) > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fillDeclaredFromSystem}
                      className="w-full rounded-xl text-xs mt-2"
                    >
                      Usar los montos del sistema
                    </Button>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500">
                  {loadingCashSummary
                    ? 'Consultando el sistema...'
                    : 'Sin datos del sistema para esta caja'}
                </p>
              )}
            </div>
            
            <div className="space-y-3">
              <div>
                <Label>Efectivo Declarado</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={closingData.declared_cash}
                  onChange={(e) => setClosingData({...closingData, declared_cash: e.target.value})}
                  placeholder="0.00"
                  className="rounded-xl"
                />
              </div>
              
              <div>
                <Label>Tarjeta Declarada</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={closingData.declared_card}
                  onChange={(e) => setClosingData({...closingData, declared_card: e.target.value})}
                  placeholder="0.00"
                  className="rounded-xl"
                />
              </div>
              
              <div>
                <Label>Transferencias Declaradas</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={closingData.declared_transfer}
                  onChange={(e) => setClosingData({...closingData, declared_transfer: e.target.value})}
                  placeholder="0.00"
                  className="rounded-xl"
                />
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-xl">
              <p className="text-sm font-semibold">
                Total Declarado: ${formatAmount(declaredPreview)}
              </p>
              {cashSummary && (
                <p className={`text-xs mt-1 font-medium ${
                  Math.abs(previewDifference) < 0.01
                    ? 'text-green-600'
                    : previewDifference > 0 ? 'text-orange-600' : 'text-red-600'
                }`}>
                  {Math.abs(previewDifference) < 0.01
                    ? 'Cuadre exacto con el sistema'
                    : `${previewDifference > 0 ? 'Sobrante' : 'Faltante'} de $${formatAmount(Math.abs(previewDifference))} vs el sistema`}
                </p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowCloseCashDialog(false)}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCloseCash}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-xl"
              >
                {loading ? 'Cerrando...' : 'Cerrar Caja'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Reporte de Cierre de Caja */}
      <Dialog open={showCashReportDialog} onOpenChange={setShowCashReportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
              <Receipt className="w-6 h-6 text-green-500" />
              Reporte de Cierre de Caja
            </DialogTitle>
          </DialogHeader>
          
          {cashReport && (
            <div className="space-y-4">
              {/* Información de la sesión */}
              <div className="bg-gray-50 p-3 rounded-xl text-sm">
                <p><strong>Cajero:</strong> {cashReport.cashier_name}</p>
                <p><strong>Apertura:</strong> {new Date(cashReport.opening_time).toLocaleString('es-ES')}</p>
                <p><strong>Cierre:</strong> {new Date(cashReport.closing_time).toLocaleString('es-ES')}</p>
              </div>

              {/* Comparativa Sistema vs Declarado */}
              <div className="grid grid-cols-2 gap-4">
                {/* Ventas del Sistema */}
                <div className="bg-blue-50 p-4 rounded-xl">
                  <h4 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />
                    Sistema
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Efectivo:</span>
                      <span className="font-medium">${formatAmount(cashReport.system_cash)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tarjeta:</span>
                      <span className="font-medium">${formatAmount(cashReport.system_card)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Transferencia:</span>
                      <span className="font-medium">${formatAmount(cashReport.system_transfer)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 mt-2">
                      <span className="font-bold">Total:</span>
                      <span className="font-bold text-blue-600">${formatAmount(cashReport.system_total)}</span>
                    </div>
                    <p className="text-xs text-gray-500 pt-1">
                      {cashReport.tickets_count ?? 0} cobro(s) · Efectivo esperado en el cajón:
                      ${formatAmount(cashReport.expected_cash)}
                    </p>
                  </div>
                </div>

                {/* Declarado por Cajero */}
                <div className="bg-purple-50 p-4 rounded-xl">
                  <h4 className="font-semibold text-purple-700 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Declarado
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Efectivo:</span>
                      <span className="font-medium">${formatAmount(cashReport.declared_cash)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tarjeta:</span>
                      <span className="font-medium">${formatAmount(cashReport.declared_card)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Transferencia:</span>
                      <span className="font-medium">${formatAmount(cashReport.declared_transfer)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-2 mt-2">
                      <span className="font-bold">Total:</span>
                      <span className="font-bold text-purple-600">${formatAmount(cashReport.declared_total)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Diferencia */}
              <div className={`p-4 rounded-xl text-center ${
                (cashReport.difference || 0) >= 0 
                  ? 'bg-green-100 border-2 border-green-300' 
                  : 'bg-red-100 border-2 border-red-300'
              }`}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  {(cashReport.difference || 0) >= 0 ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                  <span className={`font-semibold ${
                    (cashReport.difference || 0) >= 0 ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {(cashReport.difference || 0) >= 0 ? 'SOBRANTE' : 'FALTANTE'}
                  </span>
                </div>
                <p className={`text-3xl font-bold ${
                  (cashReport.difference || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  ${formatAmount(Math.abs(cashReport.difference || 0))}
                </p>
              </div>

              {/* Botones */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    // Cerrar solo el reporte; permitir que el cajero pueda cerrar sesión sin abrir nuevamente la caja
                    setShowCashReportDialog(false);
                    setCashReport(null);
                    setCashSummary(null);
                  }}
                  className="flex-1 rounded-xl"
                >
                  Cerrar
                </Button>
                <Button
                  onClick={() => printCashReportWithFallback(cashReport)}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir Reporte
                </Button>
              </div>

              {/* Cerrar sesión directo tras el cierre de caja, sin volver a quedar
                  atrapado en el modal forzado de "Abrir Caja" */}
              <Button
                variant="outline"
                onClick={logout}
                disabled={isLoggingOut}
                className="w-full mt-2 rounded-xl h-11 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {isLoggingOut ? 'Cerrando sesión...' : 'Cerrar Sesión'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer Proof Viewer Modal */}
      <Dialog open={showTransferProof} onOpenChange={setShowTransferProof}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-blue-600" />
              Comprobante de Transferencia
            </DialogTitle>
          </DialogHeader>
          {currentTransferProof && (
            <div className="space-y-4">
              <div className="relative rounded-xl overflow-hidden border-2 border-blue-200">
                <img 
                  src={currentTransferProof} 
                  alt="Comprobante de transferencia"
                  className="w-full h-auto max-h-[60vh] object-contain bg-gray-50"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowTransferProof(false)}
                  className="flex-1 rounded-xl"
                >
                  Cerrar
                </Button>
                <Button
                  onClick={() => {
                    // Open image in new tab for better viewing/printing
                    const newWindow = window.open('', '_blank');
                    newWindow.document.write(`
                      <html>
                        <head><title>Comprobante de Transferencia</title></head>
                        <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;">
                          <img src="${currentTransferProof}" style="max-width:100%;max-height:100vh;object-fit:contain;" />
                        </body>
                      </html>
                    `);
                  }}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-xl"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Abrir/Imprimir
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Order History Modal */}
      <OrderHistoryModal 
        isOpen={showOrderHistory}
        onClose={() => setShowOrderHistory(false)}
        getAuthHeaders={getAuthHeaders}
        tenantInfo={tenantInfo}
      />

      {/* Create Customer Dialog */}
      <Dialog open={showCreateCustomerDialog} onOpenChange={setShowCreateCustomerDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Crear Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-customer-name">
                Nombre Completo <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-customer-name"
                value={newCustomerData.name}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                placeholder="Juan Pérez"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="new-customer-phone">
                Teléfono <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-customer-phone"
                value={newCustomerData.phone}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                placeholder="8095551234"
                className="rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="new-customer-email">Email (opcional)</Label>
              <Input
                id="new-customer-email"
                type="email"
                value={newCustomerData.email}
                onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                placeholder="cliente@ejemplo.com"
                className="rounded-xl"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateCustomerDialog(false);
                  setNewCustomerData({ name: '', phone: '', email: '' });
                }}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateNewCustomer}
                disabled={loading || !newCustomerData.name || !newCustomerData.phone}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
              >
                {loading ? 'Creando...' : 'Crear Cliente'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Seleccionar Variante */}
      <Dialog open={showVariantSelector} onOpenChange={setShowVariantSelector}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Seleccionar Variante</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedProduct && (
              <>
                <div className="bg-gray-50 p-3 rounded-xl">
                  <p className="font-semibold text-gray-800">{selectedProduct.name}</p>
                  <p className="text-sm text-gray-600">{selectedProduct.description}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>Elige una opción:</Label>
                  {selectedProduct.variants && selectedProduct.variants.map((variant) => {
                    const soldOut = variant.tracked === true && Number(variant.stock) <= 0;
                    const disabled = !variant.is_available || soldOut;
                    return (
                      <button
                        key={variant.id}
                        onClick={() => setSelectedVariant(variant)}
                        disabled={disabled}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                          selectedVariant?.id === variant.id
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-orange-300'
                        } ${
                          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-semibold text-gray-800">{variant.name}</p>
                            {!variant.is_available && (
                              <p className="text-xs text-red-500">No disponible</p>
                            )}
                            {variant.is_available && soldOut && (
                              <p className="text-xs text-red-500">Agotada</p>
                            )}
                            {variant.is_available && !soldOut && variant.tracked === true && (
                              <p className="text-xs text-gray-500">Existencia: {Number(variant.stock) || 0}</p>
                            )}
                          </div>
                          <p className="text-xl font-bold text-orange-600">${formatAmount(variant.price)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowVariantSelector(false)}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                onClick={addVariantToCart}
                disabled={!selectedVariant}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
              >
                Agregar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Configurar ID de Caja POS */}
      <Dialog open={showPosIdDialog} onOpenChange={setShowPosIdDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-orange-500" />
              ID de Caja POS
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              Asigna un identificador único a esta caja. Debe coincidir con el configurado en la impresora del Admin.
            </p>
            <input
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="Ej: caja-01"
              value={posIdInput}
              onChange={(e) => setPosIdInput(e.target.value)}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPosIdDialog(false)}
                className="flex-1 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  const id = posIdInput.trim();
                  if (id) {
                    localStorage.setItem('pos_id', id);
                    setPosId(id);
                  } else {
                    localStorage.removeItem('pos_id');
                    setPosId('');
                  }
                  setShowPosIdDialog(false);
                  toast.success(id ? `ID de caja guardado: ${id}` : 'ID de caja eliminado');
                }}
                className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 rounded-xl"
              >
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default POSDashboard;